import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTrackerAuth } from "@/contexts/TrackerAuthContext";
import { toast } from "sonner";
import {
  Search, LayoutGrid, Columns3, GanttChart, Table2, ChevronDown, ChevronRight,
  ArrowUpDown, Users, Clock, CheckCircle2, AlertTriangle, TrendingUp, FolderOpen,
  GripVertical, Calendar as CalendarIcon, X, ChevronLeft, Eye, Upload, Sun, Moon, LogOut,
  FileSpreadsheet, Trash2, Check, Crosshair, Layers, RefreshCw, SlidersHorizontal,
  ArrowDown, ArrowUp, EyeOff, CalendarDays, BarChart3, FolderKanban, Mail, Loader2, MessageSquare,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parse, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay, isSameMonth, addMonths, subMonths } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Theme ── */
const cn = (...c) => c.filter(Boolean).join(" ");
const T = (d) => ({
  bg: d ? "bg-[#0f1117]" : "bg-slate-50",
  card: d ? "bg-[#1a1d27] border-[#2a2d3a]" : "bg-white border-slate-200/80",
  cardHover: d ? "hover:border-blue-500/40 hover:shadow-blue-900/10" : "hover:border-blue-300/60",
  text: d ? "text-slate-100" : "text-slate-900",
  textSec: d ? "text-slate-400" : "text-slate-500",
  textMuted: d ? "text-slate-500" : "text-slate-400",
  border: d ? "border-[#2a2d3a]" : "border-slate-200",
  surface: d ? "bg-[#13151d]" : "bg-slate-50",
  inputBg: d ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-100" : "",
  hoverRow: d ? "hover:bg-[#1f2230]" : "hover:bg-slate-50/60",
});

/* ── Helpers ── */
const pctColor = (pct, d) => pct >= 90 ? (d?"text-emerald-400":"text-emerald-600") : pct >= 60 ? (d?"text-blue-400":"text-blue-600") : pct >= 30 ? (d?"text-amber-400":"text-amber-600") : (d?"text-red-400":"text-red-500");
const pctBg = (pct) => pct >= 90 ? "bg-emerald-500" : pct >= 60 ? "bg-blue-500" : pct >= 30 ? "bg-amber-500" : "bg-red-500";
const statusDot = { Completed:"bg-emerald-500", "In Progress":"bg-blue-500", Pending:"bg-slate-400" };
const statusBg = (s, d) => s === "Completed" ? (d?"bg-emerald-500/15 text-emerald-400 border-emerald-500/30":"bg-emerald-50 text-emerald-700 border-emerald-200") : s === "In Progress" ? (d?"bg-blue-500/15 text-blue-400 border-blue-500/30":"bg-blue-50 text-blue-700 border-blue-200") : (d?"bg-slate-500/15 text-slate-400 border-slate-500/30":"bg-slate-100 text-slate-600 border-slate-200");

/* ── Resource color map (consistent colors per resource) ── */
const RES_COLORS = ["bg-blue-500","bg-violet-500","bg-emerald-500","bg-amber-500","bg-rose-500","bg-cyan-500","bg-fuchsia-500","bg-lime-500","bg-indigo-500","bg-orange-500","bg-teal-500","bg-pink-500"];
const RES_TEXT = ["text-blue-400","text-violet-400","text-emerald-400","text-amber-400","text-rose-400","text-cyan-400","text-fuchsia-400","text-lime-400","text-indigo-400","text-orange-400","text-teal-400","text-pink-400"];
const resColor = (email, idx) => RES_COLORS[(idx || Math.abs([...email].reduce((a,c)=>a+c.charCodeAt(0),0))) % RES_COLORS.length];
const resText = (email, idx) => RES_TEXT[(idx || Math.abs([...email].reduce((a,c)=>a+c.charCodeAt(0),0))) % RES_TEXT.length];

/* ── Date Picker Button ── */
const DatePickerButton = ({ value, onChange, placeholder = "Pick date", dark }) => {
  const t = T(dark);
  const [open, setOpen] = useState(false);
  const dateObj = value ? new Date(value + "T00:00:00") : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm"
          className={cn("h-8 w-[140px] justify-start text-left text-[11px] font-normal gap-1.5",
            dark ? "border-[#2a2d3a] bg-[#1a1d27] text-slate-300 hover:bg-[#2a2d3a]" : "",
            !value && (dark ? "text-slate-500" : "text-slate-400"))}
          data-testid={`datepicker-${placeholder.toLowerCase().replace(/\s/g,'-')}`}>
          <CalendarIcon className="w-3.5 h-3.5 shrink-0 opacity-60" />
          {value ? format(dateObj, "MMM dd, yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-auto p-0", dark && "bg-[#1a1d27] border-[#2a2d3a]")} align="start">
        <Calendar mode="single" selected={dateObj}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
          initialFocus className={dark ? "[&_.rdp-day]:text-slate-300 [&_.rdp-caption_label]:text-slate-200" : ""} />
        {value && (
          <div className={cn("border-t px-3 py-2", dark ? "border-[#2a2d3a]" : "")}>
            <Button variant="ghost" size="sm" className="h-7 w-full text-[11px] text-red-500" onClick={() => { onChange(""); setOpen(false); }}>
              <X className="w-3 h-3 mr-1" /> Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/* ── Resource Avatar ── */
const ResAvatar = ({ name, email, size = "w-6 h-6", textSize = "text-[9px]", dark }) => (
  <TooltipProvider><Tooltip><TooltipTrigger>
    <div className={cn(size, "rounded-full flex items-center justify-center font-bold uppercase shrink-0", dark ? `${resColor(email)} bg-opacity-25 ${resText(email)}` : `${resColor(email)} text-white`)} style={{fontSize: textSize === "text-[9px]" ? "9px" : "11px"}}>
      {name?.charAt(0) || "?"}
    </div>
  </TooltipTrigger><TooltipContent><p className="text-xs">{name}</p></TooltipContent></Tooltip></TooltipProvider>
);

/* ── Circular Progress ── */
const CircularProgress = ({ pct, size = 48, dark }) => {
  const r = (size - 6) / 2; const circ = 2 * Math.PI * r; const offset = circ - (pct / 100) * circ;
  const color = pct >= 90 ? "#10b981" : pct >= 60 ? "#3b82f6" : pct >= 30 ? "#f59e0b" : "#ef4444";
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={dark?"#2a2d3a":"#e2e8f0"} strokeWidth="5"/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="5" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} className="transition-all duration-700"/>
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central" style={{fontSize:"11px",fontWeight:700}} fill={dark?"#e2e8f0":"#334155"}>{Math.round(pct)}%</text>
    </svg>
  );
};

/* ── KPI ── */
const KpiCard = ({ label, value, sub, icon: Icon, color, dark }) => {
  const t = T(dark);
  return (
    <div className={cn("rounded-xl border p-4 flex items-center gap-3 shadow-sm transition-all", t.card, t.cardHover)} data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", dark ? `bg-${color}-500/20` : `bg-${color}-500`)}>
        <Icon className={cn("w-5 h-5", dark ? `text-${color}-400` : "text-white")} />
      </div>
      <div className="min-w-0">
        <p className={cn("text-[10px] font-semibold uppercase tracking-wider", t.textMuted)}>{label}</p>
        <p className={cn("text-xl font-bold tracking-tight", t.text)}>{value}</p>
        {sub && <p className={cn("text-[11px]", t.textSec)}>{sub}</p>}
      </div>
    </div>
  );
};


/* ═══════ VIEW 1: OVERVIEW - Who's Working on What ═══════ */
const OverviewView = ({ workload, projects, kpis, dark, onSelectProject }) => {
  const t = T(dark);
  const [cardTypeFilter, setCardTypeFilter] = useState({});
  if (!workload?.resources?.length && !projects.length) {
    return <div className="flex flex-col items-center justify-center py-20"><Upload className={cn("w-12 h-12 mb-3", dark?"text-slate-600":"text-slate-300")} /><p className={cn("text-sm font-medium", t.textSec)}>No projects yet</p><p className={cn("text-xs mt-1", t.textMuted)}>Upload an Excel file to get started</p></div>;
  }

  const activeResources = (workload?.resources || []).filter(r => r.in_progress > 0 || r.pending > 0);
  const completedResources = (workload?.resources || []).filter(r => r.in_progress === 0 && r.pending === 0 && r.completed > 0);

  const getTaskTypes = (tasks) => {
    const types = new Set();
    (tasks || []).forEach(t => { if (t.task_type) types.add(t.task_type); });
    return [...types].sort();
  };

  const getFilteredTasks = (email, tasks) => {
    const filter = cardTypeFilter[email];
    if (!filter) return tasks || [];
    return (tasks || []).filter(t => t.task_type === filter);
  };

  return (
    <div className="space-y-6" data-testid="overview-grid">
      {/* Active Work Section */}
      <div>
        <h2 className={cn("text-sm font-semibold mb-3 flex items-center gap-2", t.text)}>
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" /> Currently Active ({activeResources.length} resources)
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {activeResources.map((r, ri) => {
            const taskTypes = getTaskTypes(r.active_tasks);
            const filteredTasks = getFilteredTasks(r.email, r.active_tasks);
            return (
            <div key={r.email} className={cn("rounded-xl border p-4 transition-all flex flex-col", t.card, t.cardHover)} data-testid={`resource-card-${r.email}`}>
              {/* Resource Header */}
              <div className="flex items-center gap-3 mb-2">
                <ResAvatar name={r.name} email={r.email} size="w-10 h-10" textSize="text-[13px]" dark={dark} />
                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-semibold", t.text)}>{r.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={cn("text-[11px]", t.textSec)}>{r.in_progress} active · {r.pending} pending · {r.completed} done</span>
                    {r.overdue > 0 && <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-400 border-red-500/30">{r.overdue} overdue</Badge>}
                  </div>
                </div>
                <CircularProgress pct={r.completion_pct} size={44} dark={dark} />
              </div>

              {/* Task Type Filter Pills */}
              {taskTypes.length > 1 && (
                <div className={cn("flex items-center gap-1 flex-wrap mb-2 pb-2 border-b", dark ? "border-[#2a2d3a]" : "border-slate-100")}>
                  <span className={cn("text-[9px] font-semibold uppercase tracking-wider mr-1", t.textMuted)}>Type:</span>
                  <button onClick={() => setCardTypeFilter(prev => ({ ...prev, [r.email]: undefined }))}
                    className={cn("text-[10px] px-2 py-0.5 rounded-md font-medium transition-all",
                      !cardTypeFilter[r.email] ? (dark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700") : (dark ? "text-slate-500 hover:text-slate-300 bg-[#13151d]" : "text-slate-500 hover:text-slate-700 bg-slate-50"))}
                    data-testid={`card-type-all-${r.email}`}>
                    All ({(r.active_tasks || []).length})
                  </button>
                  {taskTypes.map(tt => {
                    const count = (r.active_tasks || []).filter(t => t.task_type === tt).length;
                    return (
                      <button key={tt} onClick={() => setCardTypeFilter(prev => ({ ...prev, [r.email]: tt }))}
                        className={cn("text-[10px] px-2 py-0.5 rounded-md font-medium transition-all",
                          cardTypeFilter[r.email] === tt ? (dark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700") : (dark ? "text-slate-500 hover:text-slate-300 bg-[#13151d]" : "text-slate-500 hover:text-slate-700 bg-slate-50"))}
                        data-testid={`card-type-${tt}-${r.email}`}>
                        {tt} ({count})
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Active Tasks — scrollable */}
              <div className="space-y-1.5 overflow-y-auto max-h-[260px] flex-1 pr-0.5" style={{ scrollbarWidth: "thin" }}>
                {filteredTasks.map((task, i) => (
                  <div key={task.id || i} className={cn("flex items-center gap-2 p-2 rounded-lg transition-colors", dark ? "bg-[#13151d] hover:bg-[#1f2230]" : "bg-slate-50 hover:bg-slate-100")}>
                    <div className={cn("w-1.5 h-1.5 rounded-full shrink-0", statusDot[task.status])} />
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-[12px] font-medium truncate", dark ? "text-slate-300" : "text-slate-700")}>{task.project}</p>
                      <p className={cn("text-[10px] truncate", t.textMuted)}>{task.episode} · {task.task_type} · {task.language}</p>
                    </div>
                    <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0 shrink-0", statusBg(task.status, dark))}>{task.status === "In Progress" ? "Active" : "Pending"}</Badge>
                    {task.due_date && <span className={cn("text-[10px] shrink-0", task.due_date < new Date().toISOString().slice(0,10) ? "text-red-400 font-semibold" : t.textMuted)}>{task.due_date}</span>}
                  </div>
                ))}
                {filteredTasks.length === 0 && <p className={cn("text-[11px] text-center py-4", t.textMuted)}>No tasks for this filter</p>}
              </div>

              {/* Projects this resource is on */}
              <div className={cn("flex items-center gap-1.5 mt-3 pt-2.5 border-t flex-wrap", dark ? "border-[#2a2d3a]" : "border-slate-100")}>
                <span className={cn("text-[10px] font-medium", t.textMuted)}>Projects:</span>
                {r.projects?.slice(0, 4).map(p => (
                  <button key={p} onClick={() => onSelectProject({ title: p })} className={cn("text-[10px] px-1.5 py-0.5 rounded transition-colors cursor-pointer", dark ? "bg-[#2a2d3a] text-slate-300 hover:bg-blue-500/20 hover:text-blue-400" : "bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600")}>{p}</button>
                ))}
                {r.projects?.length > 4 && <span className={cn("text-[10px]", t.textMuted)}>+{r.projects.length - 4}</span>}
              </div>
            </div>
          );
          })}
        </div>
      </div>

      {/* Projects Summary - compact cards */}
      <div>
        <h2 className={cn("text-sm font-semibold mb-3 flex items-center gap-2", t.text)}>
          <FolderOpen className="w-4 h-4" /> Projects ({projects.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {projects.slice(0, 12).map(p => (
            <div key={p.title} onClick={() => onSelectProject(p)}
              className={cn("group rounded-xl border p-4 cursor-pointer transition-all hover:shadow-lg", t.card, t.cardHover)}
              data-testid={`project-card-${p.title.replace(/\s/g,'-')}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className={cn("text-[13px] font-semibold truncate flex-1 group-hover:text-blue-500 transition-colors", t.text)}>{p.title}</h3>
                <CircularProgress pct={p.completion_pct} size={38} dark={dark} />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className={cn("text-[11px]", t.textSec)}>{p.total_tasks} tasks · {p.episode_count} eps</span>
                {p.overdue_count > 0 && <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-400 border-red-500/30">{p.overdue_count} late</Badge>}
              </div>
              {/* Resource avatars */}
              <div className="flex items-center gap-1">
                {p.top_resources?.slice(0, 5).map((r, i) => (
                  <ResAvatar key={r.email} name={r.name} email={r.email} dark={dark} />
                ))}
                {p.resource_count > 5 && <span className={cn("text-[10px] ml-1", t.textMuted)}>+{p.resource_count - 5}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Completed resources */}
      {completedResources.length > 0 && (
        <div>
          <h2 className={cn("text-sm font-semibold mb-3 flex items-center gap-2", t.text)}>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" /> All Done ({completedResources.length} resources)
          </h2>
          <div className="flex flex-wrap gap-2">
            {completedResources.map(r => (
              <div key={r.email} className={cn("flex items-center gap-2 rounded-lg border px-3 py-2", t.card)}>
                <ResAvatar name={r.name} email={r.email} dark={dark} />
                <div><p className={cn("text-[12px] font-medium", t.text)}>{r.name}</p><p className={cn("text-[10px]", t.textMuted)}>{r.completed} completed</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


/* ═══════ VIEW 2: KANBAN ═══════ */
const KanbanView = ({ kanbanData, onStatusChange, dark }) => {
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [hidePending, setHidePending] = useState(false);
  const [sortBy, setSortBy] = useState("due_date");
  const [sortDir, setSortDir] = useState("asc");
  const t = T(dark);

  const colMeta = {
    Pending: { color: dark?"border-t-slate-500":"border-t-slate-400", bg: dark?"bg-[#13151d]":"bg-slate-50", icon: Clock },
    "In Progress": { color: "border-t-blue-500", bg: dark?"bg-blue-500/5":"bg-blue-50/50", icon: TrendingUp },
    Completed: { color: "border-t-emerald-500", bg: dark?"bg-emerald-500/5":"bg-emerald-50/50", icon: CheckCircle2 },
  };

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };

  const sortCards = (cards) => {
    const arr = [...cards];
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortBy === "due_date") arr.sort((a,b) => dir * (a.due_date||"9999").localeCompare(b.due_date||"9999"));
    else if (sortBy === "project") arr.sort((a,b) => dir * (a.title||"").localeCompare(b.title||""));
    else if (sortBy === "resource") arr.sort((a,b) => dir * (a.resource_name||"").localeCompare(b.resource_name||""));
    else if (sortBy === "language") arr.sort((a,b) => dir * (a.language||"").localeCompare(b.language||""));
    return arr;
  };

  if (!kanbanData?.columns) return null;
  const visibleCols = hidePending ? ["In Progress", "Completed"] : ["Pending", "In Progress", "Completed"];

  return (
    <div data-testid="kanban-board">
      {/* Kanban controls */}
      <div className={cn("flex items-center justify-between mb-4 p-3 rounded-xl border", t.card)}>
        <div className="flex items-center gap-2">
          <SlidersHorizontal className={cn("w-4 h-4", t.textMuted)} />
          <span className={cn("text-[11px] font-medium", t.textSec)}>Sort by:</span>
          {["due_date","project","resource","language"].map(s => (
            <button key={s} onClick={() => handleSort(s)}
              className={cn("flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md transition-all font-medium",
                sortBy === s ? (dark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700") : (dark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"))}
              data-testid={`sort-${s}`}>
              {s === "due_date" ? "Due Date" : s.charAt(0).toUpperCase() + s.slice(1)}
              {sortBy === s && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
            </button>
          ))}
        </div>
        <button onClick={() => setHidePending(!hidePending)}
          className={cn("flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md transition-all font-medium",
            hidePending ? (dark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700") : (dark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"))}
          data-testid="toggle-pending">
          {hidePending ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {hidePending ? "Show Pending" : "Hide Pending"}
        </button>
      </div>

      <div className={cn("grid gap-5", hidePending ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1 lg:grid-cols-3")}>
        {visibleCols.map(col => {
          const m = colMeta[col]; const Icon = m.icon;
          const cards = sortCards(kanbanData.columns[col] || []);
          return (
            <div key={col}
              className={cn("rounded-xl border-t-4", m.color, m.bg, "border", t.border, "min-h-[400px] flex flex-col", dragOver===col && "ring-2 ring-blue-400 ring-offset-2")}
              onDragOver={e=>{e.preventDefault();setDragOver(col)}} onDragLeave={()=>setDragOver(null)}
              onDrop={e=>{e.preventDefault();setDragOver(null);if(dragging&&dragging.fromCol!==col)onStatusChange(dragging.taskId,col);setDragging(null)}}
              data-testid={`kanban-col-${col.replace(/\s/g,'-').toLowerCase()}`}>
              <div className={cn("flex items-center justify-between px-4 py-3 border-b", t.border)}>
                <div className="flex items-center gap-2"><Icon className={cn("w-4 h-4", t.textSec)} /><h3 className={cn("font-semibold text-sm", t.text)}>{col}</h3></div>
                <Badge variant="secondary" className={cn("text-[11px]", dark && "bg-[#2a2d3a] text-slate-300")}>{cards.length}</Badge>
              </div>
              <div className="flex-1 p-3 space-y-2.5 overflow-y-auto max-h-[600px]">
                {cards.slice(0,100).map(card => (
                  <div key={card.id} draggable onDragStart={e=>{setDragging({taskId:card.id,fromCol:col});e.dataTransfer.effectAllowed="move"}}
                    className={cn("rounded-lg border p-3.5 cursor-grab active:cursor-grabbing transition-all group", t.card, "hover:shadow-md")} data-testid={`kanban-card-${card.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className={cn("text-[13px] font-medium truncate", t.text)}>{card.title}</p>
                        <p className={cn("text-[11px] truncate", t.textSec)}>{card.episode}</p>
                      </div>
                      <GripVertical className="w-4 h-4 text-slate-500 shrink-0 opacity-0 group-hover:opacity-100" />
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                      <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", dark && "border-[#2a2d3a] text-slate-400")}>{card.task_type}</Badge>
                      <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", dark && "border-[#2a2d3a] text-slate-400")}>{card.language}</Badge>
                    </div>
                    {/* Resource highlight */}
                    <div className={cn("flex items-center justify-between pt-2 border-t", dark?"border-[#2a2d3a]":"border-slate-100")}>
                      <div className="flex items-center gap-2">
                        <ResAvatar name={card.resource_name} email={card.resource} dark={dark} />
                        <span className={cn("text-[12px] font-medium truncate max-w-[100px]", dark?"text-slate-300":"text-slate-700")}>{card.resource_name}</span>
                      </div>
                      {card.due_date && (
                        <span className={cn("text-[10px] flex items-center gap-0.5 shrink-0", card.due_date < new Date().toISOString().slice(0,10) ? "text-red-400 font-semibold" : t.textMuted)}>
                          <CalendarIcon className="w-3 h-3" />{card.due_date}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {cards.length>100 && <p className={cn("text-center text-xs py-2", t.textMuted)}>+{cards.length-100} more</p>}
                {cards.length===0 && <div className="flex flex-col items-center justify-center py-10"><Icon className={cn("w-8 h-8 mb-2 opacity-40", t.textMuted)} /><p className={cn("text-xs", t.textMuted)}>No tasks</p></div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


/* ═══════ VIEW 3: TIMELINE (Gantt + Calendar modes) ═══════ */
const CalendarMonthView = ({ timeline, dark }) => {
  const t = T(dark);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const allTasks = useMemo(() => {
    const tasks = [];
    (timeline || []).forEach(p => {
      (p.tasks || []).forEach(task => {
        if (task.due_date) tasks.push({ ...task, project: p.title });
      });
    });
    return tasks;
  }, [timeline]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = getDay(monthStart);

  const tasksByDate = useMemo(() => {
    const map = {};
    allTasks.forEach(task => {
      const d = task.due_date;
      if (!map[d]) map[d] = [];
      map[d].push(task);
    });
    return map;
  }, [allTasks]);

  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = new Date();

  return (
    <div className={cn("rounded-xl border overflow-hidden", t.card)} data-testid="calendar-month-view">
      {/* Calendar header */}
      <div className={cn("flex items-center justify-between px-4 py-3 border-b", t.border)}>
        <Button variant="ghost" size="sm" className={cn("h-8", dark && "text-slate-400 hover:text-white")} onClick={() => setCurrentMonth(m => subMonths(m, 1))} data-testid="cal-prev-month">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className={cn("text-sm font-semibold", t.text)}>{format(currentMonth, "MMMM yyyy")}</h3>
        <Button variant="ghost" size="sm" className={cn("h-8", dark && "text-slate-400 hover:text-white")} onClick={() => setCurrentMonth(m => addMonths(m, 1))} data-testid="cal-next-month">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Weekday headers */}
      <div className={cn("grid grid-cols-7 border-b", t.border, t.surface)}>
        {weekdays.map(d => (
          <div key={d} className={cn("px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wider", t.textMuted)}>{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {/* Empty cells for offset */}
        {Array.from({ length: startDay }).map((_, i) => (
          <div key={`empty-${i}`} className={cn("min-h-[100px] border-b border-r", t.border, dark ? "bg-[#0d0f15]" : "bg-slate-50/50")} />
        ))}
        {days.map(day => {
          const dateStr = format(day, "yyyy-MM-dd");
          const dayTasks = tasksByDate[dateStr] || [];
          const isToday = isSameDay(day, today);
          return (
            <div key={dateStr} className={cn("min-h-[100px] border-b border-r p-1.5 transition-colors", t.border,
              isToday ? (dark ? "bg-blue-500/5" : "bg-blue-50/60") : "",
              dark ? "hover:bg-[#1f2230]" : "hover:bg-slate-50")}
              data-testid={`cal-day-${dateStr}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={cn("text-[11px] font-medium w-6 h-6 flex items-center justify-center rounded-full",
                  isToday ? "bg-blue-500 text-white" : t.textSec)}>
                  {format(day, "d")}
                </span>
                {dayTasks.length > 0 && <span className={cn("text-[9px] font-semibold", t.textMuted)}>{dayTasks.length}</span>}
              </div>
              <div className="space-y-0.5 overflow-y-auto max-h-[72px]">
                {dayTasks.slice(0, 4).map((task, i) => (
                  <TooltipProvider key={task.id || i}><Tooltip><TooltipTrigger asChild>
                    <div className={cn("text-[9px] px-1.5 py-0.5 rounded truncate cursor-default",
                      task.status === "Completed" ? (dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700") :
                      task.status === "In Progress" ? (dark ? "bg-blue-500/15 text-blue-400" : "bg-blue-50 text-blue-700") :
                      (dark ? "bg-slate-500/15 text-slate-400" : "bg-slate-100 text-slate-600"))}>
                      {task.resource_name?.split(" ")[0]} · {task.task_type}
                    </div>
                  </TooltipTrigger><TooltipContent side="top">
                    <div className="text-[11px]">
                      <p className="font-medium">{task.project}</p>
                      <p className="text-slate-400">{task.episode} · {task.task_type} · {task.language}</p>
                      <p className="text-slate-400">{task.resource_name} — {task.status}</p>
                    </div>
                  </TooltipContent></Tooltip></TooltipProvider>
                ))}
                {dayTasks.length > 4 && <p className={cn("text-[8px] text-center", t.textMuted)}>+{dayTasks.length - 4} more</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className={cn("flex items-center gap-4 px-4 py-2.5 border-t", t.border, t.surface)}>
        <span className={cn("text-[10px] font-medium", t.textMuted)}>Status:</span>
        {[{ label: "Completed", cls: dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700" },
          { label: "In Progress", cls: dark ? "bg-blue-500/15 text-blue-400" : "bg-blue-50 text-blue-700" },
          { label: "Pending", cls: dark ? "bg-slate-500/15 text-slate-400" : "bg-slate-100 text-slate-600" }].map(s => (
          <span key={s.label} className={cn("text-[10px] px-2 py-0.5 rounded", s.cls)}>{s.label}</span>
        ))}
        <span className={cn("text-[10px] ml-auto", t.textMuted)}>{allTasks.length} tasks with due dates</span>
      </div>
    </div>
  );
};

const GanttView = ({ timeline, dark }) => {
  const [expanded, setExpanded] = useState({});
  const [searchTl, setSearchTl] = useState("");
  const t = T(dark);

  if (!timeline?.length) return <div className="flex flex-col items-center py-20"><GanttChart className={cn("w-12 h-12 mb-3",dark?"text-slate-600":"text-slate-300")} /><p className={cn("text-sm",t.textSec)}>No timeline data</p></div>;

  const items = timeline.filter(p => p.start && p.end && (!searchTl || p.title.toLowerCase().includes(searchTl.toLowerCase())));
  const allDates = items.flatMap(p => [p.start, p.end]).sort();
  const minMs = new Date(allDates[0]).getTime();
  const maxMs = new Date(allDates[allDates.length-1]).getTime();
  const range = maxMs - minMs || 1;
  const toL = d => ((new Date(d).getTime()-minMs)/range)*100;
  const toW = (s,e) => Math.max(((new Date(e).getTime()-new Date(s).getTime())/range)*100, 0.5);
  const months = (() => { const m=[]; let d=new Date(allDates[0]); d.setDate(1); const end=new Date(allDates[allDates.length-1]); while(d<=end){m.push({key:d.toISOString().slice(0,7),left:toL(d.toISOString().slice(0,10)),label:d.toLocaleDateString("en-US",{month:"short",year:"2-digit"})}); d.setMonth(d.getMonth()+1)} const step=Math.max(1,Math.floor(m.length/14)); return m.filter((_,i)=>i%step===0) })();

  const today = new Date().toISOString().slice(0,10);
  const todayLeft = toL(today);

  return (
    <div className={cn("rounded-xl border overflow-hidden", t.card)} data-testid="gantt-view">
      <div className={cn("flex items-center gap-3 px-4 py-3 border-b", t.border)}>
        <div className="relative w-60"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><Input className={cn("h-8 pl-8 text-[12px]",t.inputBg)} placeholder="Filter projects..." value={searchTl} onChange={e=>setSearchTl(e.target.value)} /></div>
        <span className={cn("text-[11px]", t.textSec)}>{items.length} projects · Due date based</span>
      </div>

      <div className={cn("flex items-center border-b px-4 py-2", t.border, t.surface)}>
        <div className={cn("w-[280px] shrink-0 text-[11px] font-semibold uppercase", t.textMuted)}>Project / Task</div>
        <div className="flex-1 relative h-6">
          {months.map(m=><span key={m.key} className={cn("absolute text-[9px] font-medium whitespace-nowrap",t.textMuted)} style={{left:`${m.left}%`}}>{m.label}</span>)}
          <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{left:`${todayLeft}%`}} />
        </div>
      </div>

      <div className="max-h-[600px] overflow-y-auto relative">
        {items.map(p => (
          <div key={p.title}>
            <div className={cn("flex items-center border-b transition-colors cursor-pointer", t.border, t.hoverRow)}
              onClick={()=>setExpanded(prev=>({...prev,[p.title]:!prev[p.title]}))}>
              <div className="w-[280px] shrink-0 px-4 py-2.5 flex items-center gap-2">
                {(p.tasks?.length > 0 || p.episodes?.length > 0) ? (expanded[p.title] ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />) : <div className="w-3.5"/>}
                <p className={cn("text-[12px] font-medium truncate flex-1",dark?"text-slate-300":"text-slate-700")}>{p.title}</p>
                <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0 shrink-0",dark?"border-[#2a2d3a]":"")}>{Math.round(p.completion_pct)}%</Badge>
              </div>
              <div className="flex-1 relative h-8 px-2">
                <div className={cn("absolute top-2 left-0 right-0 h-4 rounded",dark?"bg-[#1a1d27]":"bg-slate-50")} />
                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                  <div className={cn("absolute top-2 h-4 rounded opacity-85 hover:opacity-100 transition-opacity",pctBg(p.completion_pct))} style={{left:`${toL(p.start)}%`,width:`${toW(p.start,p.end)}%`,minWidth:"6px"}}>
                    <div className="h-full bg-white/25 rounded-l" style={{width:`${p.completion_pct}%`}} />
                  </div>
                </TooltipTrigger><TooltipContent><p className="text-xs font-medium">{p.title}</p><p className="text-[10px] text-slate-400">{p.start} → {p.end} · {p.total_tasks} tasks</p></TooltipContent></Tooltip></TooltipProvider>
              </div>
            </div>

            {expanded[p.title] && p.tasks?.map((task, idx) => (
              <div key={task.id || idx} className={cn("flex items-center border-b", dark?"border-[#1a1d27] bg-[#13151d]":"border-slate-50 bg-slate-50/40")}>
                <div className="w-[280px] shrink-0 pl-8 pr-4 py-1.5 flex items-center gap-2">
                  <ResAvatar name={task.resource_name} email={task.resource} dark={dark} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("text-[11px] font-medium truncate", dark?"text-slate-300":"text-slate-600")}>{task.resource_name}</p>
                    <p className={cn("text-[9px] truncate", t.textMuted)}>{task.episode} · {task.task_type} · {task.language}</p>
                  </div>
                  <div className={cn("w-1.5 h-1.5 rounded-full shrink-0", statusDot[task.status])} />
                </div>
                <div className="flex-1 relative h-5 px-2">
                  {task.due_date && (
                    <TooltipProvider><Tooltip><TooltipTrigger asChild>
                      <div className={cn("absolute top-1 w-3 h-3 rounded-full border-2", statusDot[task.status], dark?"border-[#13151d]":"border-white")}
                        style={{left:`${toL(task.due_date)}%`, marginLeft:"-6px"}} />
                    </TooltipTrigger><TooltipContent><p className="text-[10px]">{task.resource_name}: {task.task_type} due {task.due_date} ({task.status})</p></TooltipContent></Tooltip></TooltipProvider>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const TimelineView = ({ timeline, dark }) => {
  const [mode, setMode] = useState("gantt");
  const t = T(dark);

  if (!timeline?.length && mode === "gantt") return <div className="flex flex-col items-center py-20"><GanttChart className={cn("w-12 h-12 mb-3",dark?"text-slate-600":"text-slate-300")} /><p className={cn("text-sm",t.textSec)}>No timeline data</p></div>;

  return (
    <div data-testid="timeline-view">
      {/* Mode toggle */}
      <div className={cn("flex items-center gap-2 mb-4 p-3 rounded-xl border", t.card)}>
        <span className={cn("text-[11px] font-medium", t.textSec)}>View:</span>
        <button onClick={() => setMode("gantt")}
          className={cn("flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md transition-all font-medium",
            mode === "gantt" ? (dark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700") : (dark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"))}
          data-testid="timeline-mode-gantt">
          <GanttChart className="w-3.5 h-3.5" /> Gantt Chart
        </button>
        <button onClick={() => setMode("calendar")}
          className={cn("flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md transition-all font-medium",
            mode === "calendar" ? (dark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700") : (dark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"))}
          data-testid="timeline-mode-calendar">
          <CalendarDays className="w-3.5 h-3.5" /> Calendar
        </button>
      </div>

      {mode === "gantt" ? <GanttView timeline={timeline} dark={dark} /> : <CalendarMonthView timeline={timeline} dark={dark} />}
    </div>
  );
};


/* ═══════ VIEW 4: TABLE - Who's doing what ═══════ */
const TableView = ({ projects, onSelect, dark }) => {
  const [sortKey, setSortKey] = useState("title"); const [sortDir, setSortDir] = useState("asc");
  const t = T(dark);
  const sorted = useMemo(() => {
    const arr = [...projects]; arr.sort((a,b) => { let va=a[sortKey],vb=b[sortKey]; if(typeof va==="string")va=va.toLowerCase(); if(typeof vb==="string")vb=vb.toLowerCase(); return va<vb?(sortDir==="asc"?-1:1):va>vb?(sortDir==="asc"?1:-1):0 }); return arr;
  }, [projects, sortKey, sortDir]);
  const hs = k => { if(sortKey===k) setSortDir(d=>d==="asc"?"desc":"asc"); else{setSortKey(k);setSortDir("asc")} };
  const SortIcon = sortDir === "asc" ? ArrowUp : ArrowDown;
  const SH = ({label,field}) => (
    <th className={cn("px-4 py-3 text-[11px] font-semibold uppercase tracking-wider cursor-pointer hover:text-blue-500 transition-colors whitespace-nowrap", t.textMuted)} onClick={()=>hs(field)}>
      <div className="flex items-center gap-1">{label}{sortKey===field ? <SortIcon className="w-3 h-3 text-blue-500"/> : <ArrowUpDown className="w-3 h-3 opacity-30"/>}</div>
    </th>
  );

  return (
    <div className={cn("rounded-xl border overflow-hidden", t.card)} data-testid="table-view">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={cn("border-b", t.border, t.surface)}><tr>
            <SH label="Project" field="title" /><SH label="Tasks" field="total_tasks" /><SH label="Progress" field="completion_pct" />
            <th className={cn("px-4 py-3 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap", t.textMuted)}>Resources Working</th>
            <SH label="Episodes" field="episode_count" /><SH label="Due Date" field="latest_date" />
            <th className={cn("px-4 py-3 text-[11px] font-semibold uppercase tracking-wider", t.textMuted)}>Actions</th>
          </tr></thead>
          <tbody>
            {sorted.map(p => (
              <tr key={p.title} className={cn("border-b transition-colors", t.border, t.hoverRow)}>
                <td className="px-4 py-3">
                  <p className={cn("text-[13px] font-medium max-w-[180px] truncate", t.text)}>{p.title}</p>
                  <div className="flex gap-1 mt-0.5">{p.task_types?.slice(0,3).map(tt=><span key={tt} className={cn("text-[9px] px-1 py-0 rounded",dark?"bg-[#2a2d3a] text-slate-400":"bg-slate-100 text-slate-500")}>{tt}</span>)}</div>
                </td>
                <td className={cn("px-4 py-3 text-[13px] font-medium", t.text)}>{p.total_tasks}</td>
                <td className="px-4 py-3 w-[140px]">
                  <div className="flex items-center gap-2"><div className="flex-1"><Progress value={p.completion_pct} className="h-2"/></div><span className={cn("text-[12px] font-semibold",pctColor(p.completion_pct,dark))}>{Math.round(p.completion_pct)}%</span></div>
                </td>
                <td className="px-4 py-3 max-w-[250px]">
                  <div className="flex flex-wrap gap-1">
                    {p.top_resources?.slice(0, 4).map((r) => (
                      <span key={r.email} className={cn("inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full", dark ? "bg-[#2a2d3a] text-slate-300" : "bg-slate-100 text-slate-700")}>
                        <span className={cn("w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold uppercase shrink-0", dark ? `${resColor(r.email)} bg-opacity-25 ${resText(r.email)}` : `${resColor(r.email)} text-white`)}>{r.name?.charAt(0)}</span>
                        {r.name}
                      </span>
                    ))}
                    {p.resource_count > 4 && <span className={cn("text-[10px] px-1.5 py-0.5", t.textMuted)}>+{p.resource_count-4} more</span>}
                  </div>
                </td>
                <td className={cn("px-4 py-3 text-[13px]", t.textSec)}>{p.episode_count}</td>
                <td className={cn("px-4 py-3 text-[12px]",t.textSec)}>{p.latest_date||"-"}</td>
                <td className="px-4 py-3"><Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={()=>onSelect(p)} data-testid={`view-project-${p.title.replace(/\s/g,'-')}`}><Eye className="w-3.5 h-3.5 mr-1"/>View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


/* ═══════ VIEW 5: ANALYTICS ═══════ */
const AnalyticsView = ({ dark, headers, globalFilters = {} }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rankSort, setRankSort] = useState("total_tasks");
  const [rankDir, setRankDir] = useState("desc");
  const t = T(dark);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (globalFilters.resource) params.resource = globalFilters.resource;
    if (globalFilters.task_type) params.task_type = globalFilters.task_type;
    if (globalFilters.language) params.language = globalFilters.language;
    if (globalFilters.from_date) params.from_date = globalFilters.from_date;
    if (globalFilters.to_date) params.to_date = globalFilters.to_date;
    axios.get(`${API}/tracker/analytics`, { params, headers })
      .then(r => setData(r.data)).catch(() => toast.error("Failed to load analytics")).finally(() => setLoading(false));
  }, [globalFilters.resource, globalFilters.task_type, globalFilters.language, globalFilters.from_date, globalFilters.to_date]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const { resource_rankings, title_distribution, task_type_distribution, due_date_stats, status_summary } = data;

  const sortedRankings = [...resource_rankings].sort((a, b) => {
    const dir = rankDir === "asc" ? 1 : -1;
    return dir * ((a[rankSort] || 0) - (b[rankSort] || 0));
  });

  const maxTasks = Math.max(...resource_rankings.map(r => r.total_tasks), 1);
  const maxTitleTasks = Math.max(...title_distribution.map(t => t.total_tasks), 1);

  return (
    <div className="space-y-6" data-testid="analytics-view">
      {/* Status Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        {[
          { label: "Total Tasks", val: status_summary.total, color: "blue" },
          { label: "Completed", val: status_summary.completed, color: "emerald" },
          { label: "In Progress", val: status_summary.in_progress, color: "amber" },
          { label: "Pending", val: status_summary.pending, color: "slate" },
          { label: "Due This Week", val: due_date_stats.due_this_week, color: "orange" },
          { label: "Completion", val: `${status_summary.completion_pct}%`, color: "violet" },
        ].map(c => (
          <div key={c.label} className={cn("rounded-xl border p-3 text-center", t.card)} data-testid={`analytics-stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <p className={cn("text-2xl font-bold", dark ? `text-${c.color}-400` : `text-${c.color}-600`)}>{c.val}</p>
            <p className={cn("text-[10px] font-medium uppercase tracking-wider mt-1", t.textMuted)}>{c.label}</p>
          </div>
        ))}
      </div>

      {/* Resource Rankings */}
      <div className={cn("rounded-xl border overflow-hidden", t.card)}>
        <div className={cn("flex items-center justify-between px-4 py-3 border-b", t.border)}>
          <h3 className={cn("text-sm font-semibold flex items-center gap-2", t.text)}>
            <TrendingUp className="w-4 h-4 text-blue-500" /> Resource Rankings
          </h3>
          <div className="flex items-center gap-1.5">
            <span className={cn("text-[10px]", t.textMuted)}>Sort:</span>
            {[{ k: "total_tasks", l: "Tasks" }, { k: "completed", l: "Done" }, { k: "completion_pct", l: "Rate" }, { k: "overdue", l: "Overdue" }].map(s => (
              <button key={s.k} onClick={() => { if (rankSort === s.k) setRankDir(d => d === "asc" ? "desc" : "asc"); else { setRankSort(s.k); setRankDir("desc"); } }}
                className={cn("text-[10px] px-2 py-1 rounded-md font-medium transition-all",
                  rankSort === s.k ? (dark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700") : (dark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"))}
                data-testid={`rank-sort-${s.k}`}>
                {s.l} {rankSort === s.k && (rankDir === "asc" ? <ArrowUp className="w-3 h-3 inline" /> : <ArrowDown className="w-3 h-3 inline" />)}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={cn("border-b", t.border, t.surface)}>
              <tr>
                {["#", "Resource", "Tasks", "Completed", "In Progress", "Pending", "Overdue", "Completion", "Projects", "Types"].map(h => (
                  <th key={h} className={cn("px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-left whitespace-nowrap", t.textMuted)}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRankings.map((r, i) => (
                <tr key={r.email} className={cn("border-b transition-colors", t.border, t.hoverRow,
                  i === 0 && "bg-amber-500/5", i === 1 && "bg-slate-500/5", i === 2 && "bg-orange-500/5")}
                  data-testid={`rank-row-${r.email}`}>
                  <td className="px-3 py-2.5">
                    <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold",
                      i === 0 ? "bg-amber-500 text-white" : i === 1 ? (dark ? "bg-slate-400 text-slate-900" : "bg-slate-300 text-slate-800") :
                      i === 2 ? "bg-orange-500 text-white" : (dark ? "bg-[#2a2d3a] text-slate-400" : "bg-slate-100 text-slate-500"))}>
                      {i + 1}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <ResAvatar name={r.name} email={r.email} dark={dark} />
                      <div className="min-w-0">
                        <p className={cn("text-[12px] font-medium truncate max-w-[140px]", t.text)}>{r.name}</p>
                        <p className={cn("text-[9px] truncate max-w-[140px]", t.textMuted)}>{r.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-[12px] font-semibold", t.text)}>{r.total_tasks}</span>
                      <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: dark ? "#2a2d3a" : "#e2e8f0" }}>
                        <div className="h-full rounded-full bg-blue-500" style={{ width: `${(r.total_tasks / maxTasks) * 100}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className={cn("px-3 py-2.5 text-[12px] font-medium", dark ? "text-emerald-400" : "text-emerald-600")}>{r.completed}</td>
                  <td className={cn("px-3 py-2.5 text-[12px]", dark ? "text-blue-400" : "text-blue-600")}>{r.in_progress}</td>
                  <td className={cn("px-3 py-2.5 text-[12px]", t.textSec)}>{r.pending}</td>
                  <td className="px-3 py-2.5">
                    {r.overdue > 0 ? <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-400 border-red-500/30">{r.overdue}</Badge>
                      : <span className={cn("text-[12px]", t.textMuted)}>0</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ background: dark ? "#2a2d3a" : "#e2e8f0" }}>
                        <div className={cn("h-full rounded-full", pctBg(r.completion_pct))} style={{ width: `${r.completion_pct}%` }} />
                      </div>
                      <span className={cn("text-[11px] font-semibold", pctColor(r.completion_pct, dark))}>{r.completion_pct}%</span>
                    </div>
                  </td>
                  <td className={cn("px-3 py-2.5 text-[12px]", t.textSec)}>{r.project_count}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-0.5 flex-wrap max-w-[120px]">
                      {r.task_types.slice(0, 3).map(tt => <span key={tt} className={cn("text-[8px] px-1 py-0 rounded", dark ? "bg-[#2a2d3a] text-slate-400" : "bg-slate-100 text-slate-500")}>{tt}</span>)}
                      {r.task_types.length > 3 && <span className={cn("text-[8px]", t.textMuted)}>+{r.task_types.length - 3}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Two-column: Title Distribution + Task Type Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Title Distribution */}
        <div className={cn("rounded-xl border overflow-hidden", t.card)}>
          <div className={cn("px-4 py-3 border-b", t.border)}>
            <h3 className={cn("text-sm font-semibold flex items-center gap-2", t.text)}><FolderOpen className="w-4 h-4 text-violet-500" /> By Project ({title_distribution.length})</h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {title_distribution.map((td, i) => (
              <div key={td.title} className={cn("flex items-center gap-3 px-4 py-2.5 border-b", t.border, t.hoverRow)} data-testid={`title-dist-${i}`}>
                <span className={cn("text-[10px] w-5 text-right", t.textMuted)}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className={cn("text-[12px] font-medium truncate", t.text)}>{td.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: dark ? "#2a2d3a" : "#e2e8f0" }}>
                      <div className={cn("h-full rounded-full", pctBg(td.completion_pct))} style={{ width: `${td.completion_pct}%` }} />
                    </div>
                    <span className={cn("text-[10px] font-medium shrink-0", pctColor(td.completion_pct, dark))}>{td.completion_pct}%</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={cn("text-[12px] font-semibold", t.text)}>{td.total_tasks}</p>
                  <p className={cn("text-[9px]", t.textMuted)}>{td.resource_count} res</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Task Type Distribution */}
        <div className={cn("rounded-xl border overflow-hidden", t.card)}>
          <div className={cn("px-4 py-3 border-b", t.border)}>
            <h3 className={cn("text-sm font-semibold flex items-center gap-2", t.text)}><Layers className="w-4 h-4 text-cyan-500" /> By Task Type ({task_type_distribution.length})</h3>
          </div>
          <div className="space-y-1 p-4">
            {task_type_distribution.map(td => {
              const pct = (td.total_tasks / (status_summary.total || 1)) * 100;
              return (
                <div key={td.task_type} className={cn("rounded-lg p-3", dark ? "bg-[#13151d]" : "bg-slate-50")} data-testid={`type-dist-${td.task_type}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={cn("text-[12px] font-medium", t.text)}>{td.task_type}</span>
                    <div className="flex items-center gap-2">
                      <span className={cn("text-[12px] font-semibold", t.text)}>{td.total_tasks}</span>
                      <span className={cn("text-[10px]", t.textMuted)}>({pct.toFixed(1)}%)</span>
                    </div>
                  </div>
                  <div className="flex gap-0.5 h-3 rounded-full overflow-hidden" style={{ background: dark ? "#2a2d3a" : "#e2e8f0" }}>
                    <div className="bg-emerald-500 rounded-l-full transition-all" style={{ width: `${(td.completed / (td.total_tasks || 1)) * 100}%` }} />
                    <div className="bg-blue-500 transition-all" style={{ width: `${(td.in_progress / (td.total_tasks || 1)) * 100}%` }} />
                    <div className="bg-slate-400 rounded-r-full transition-all" style={{ width: `${(td.pending / (td.total_tasks || 1)) * 100}%` }} />
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={cn("text-[9px]", dark ? "text-emerald-400" : "text-emerald-600")}>{td.completed} done</span>
                    <span className={cn("text-[9px]", dark ? "text-blue-400" : "text-blue-600")}>{td.in_progress} active</span>
                    <span className={cn("text-[9px]", t.textMuted)}>{td.pending} pending</span>
                    <span className={cn("text-[9px] ml-auto", t.textMuted)}>{td.resource_count} resources</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Due Date Analysis */}
      {due_date_stats.by_month?.length > 0 && (
        <div className={cn("rounded-xl border overflow-hidden", t.card)}>
          <div className={cn("px-4 py-3 border-b", t.border)}>
            <h3 className={cn("text-sm font-semibold flex items-center gap-2", t.text)}><CalendarIcon className="w-4 h-4 text-amber-500" /> Due Date Timeline</h3>
          </div>
          <div className="p-4">
            <div className="flex items-end gap-1 h-[120px]">
              {due_date_stats.by_month.map(m => {
                const maxM = Math.max(...due_date_stats.by_month.map(x => x.total), 1);
                const h = (m.total / maxM) * 100;
                const compH = (m.completed / (m.total || 1)) * h;
                return (
                  <TooltipProvider key={m.month}><Tooltip><TooltipTrigger asChild>
                    <div className="flex-1 flex flex-col items-center gap-0.5">
                      <div className="w-full relative rounded-t" style={{ height: `${h}%`, minHeight: "4px" }}>
                        <div className={cn("absolute bottom-0 w-full rounded-t", dark ? "bg-[#2a2d3a]" : "bg-slate-200")} style={{ height: "100%" }} />
                        <div className="absolute bottom-0 w-full rounded-t bg-emerald-500/80" style={{ height: `${compH}%` }} />
                      </div>
                    </div>
                  </TooltipTrigger><TooltipContent>
                    <p className="text-[11px] font-medium">{m.month}</p>
                    <p className="text-[10px] text-slate-400">{m.total} tasks · {m.completed} done · {m.overdue} overdue</p>
                  </TooltipContent></Tooltip></TooltipProvider>
                );
              })}
            </div>
            <div className="flex gap-1 mt-1">
              {due_date_stats.by_month.map(m => (
                <div key={m.month} className="flex-1 text-center">
                  <span className={cn("text-[8px]", t.textMuted)}>{m.month.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/* ═══════ VIEW 6: GP PROJECTS (Database Center style) ═══════ */
const GPProjectsView = ({ dark, headers, filters: globalFilterOptions, globalFilters = {} }) => {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [gpSearch, setGpSearch] = useState("");
  const [gpProject, setGpProject] = useState("");
  const [gpResource, setGpResource] = useState("");
  const [gpType, setGpType] = useState("");
  const [gpStatus, setGpStatus] = useState("");
  const [gpDueFilter, setGpDueFilter] = useState("");
  const [gpSeason, setGpSeason] = useState("");
  const [sortBy, setSortBy] = useState("main_title");
  const [sortDir, setSortDir] = useState("asc");
  const [showColToggle, setShowColToggle] = useState(false);
  const [visibleCols, setVisibleCols] = useState({ project: true, season: true, episode: true, task: true, resource: true, language: true, due_date: true, duration: true, status: true, po: true });
  const t = T(dark);
  const toggleCol = (col) => setVisibleCols(prev => ({ ...prev, [col]: !prev[col] }));

  const fetchGP = useCallback(async () => {
    setLoading(true);
    const params = { page, page_size: 50, sort_by: sortBy, sort_dir: sortDir };
    if (gpSearch) params.search = gpSearch;
    if (gpProject) params.project = gpProject;
    if (gpResource || globalFilters.resource) params.resource = gpResource || globalFilters.resource;
    if (gpType || globalFilters.task_type) params.task_type = gpType || globalFilters.task_type;
    if (gpStatus) params.status = gpStatus;
    if (gpDueFilter) params.due_filter = gpDueFilter;
    if (gpSeason) params.season = gpSeason;
    if (globalFilters.from_date) params.from_date = globalFilters.from_date;
    if (globalFilters.to_date) params.to_date = globalFilters.to_date;
    try {
      const res = await axios.get(`${API}/tracker/gp-projects`, { params, headers });
      setTasks(res.data.tasks || []);
      setStats(res.data.stats || {});
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, [page, sortBy, sortDir, gpSearch, gpProject, gpResource, gpType, gpStatus, gpDueFilter, gpSeason, globalFilters.resource, globalFilters.task_type, globalFilters.from_date, globalFilters.to_date]);

  useEffect(() => { fetchGP(); }, [fetchGP]);
  useEffect(() => { setPage(1); }, [gpSearch, gpProject, gpResource, gpType, gpStatus, gpDueFilter]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };
  const SortIcon = sortDir === "asc" ? ArrowUp : ArrowDown;
  const SH = ({ label, field }) => (
    <th className={cn("px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider cursor-pointer hover:text-blue-500 transition-colors whitespace-nowrap", t.textMuted)}
      onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">{label}{sortBy === field ? <SortIcon className="w-3 h-3 text-blue-500" /> : <ArrowUpDown className="w-3 h-3 opacity-30" />}</div>
    </th>
  );

  const hasGpFilters = gpSearch || gpProject || gpResource || gpType || gpStatus || gpDueFilter || gpSeason;

  return (
    <div data-testid="gp-projects-view">
      {/* Stats bar */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
        {[
          { label: "Total", val: total, color: "blue" },
          { label: "Completed", val: stats.completed || 0, color: "emerald" },
          { label: "In Progress", val: stats.in_progress || 0, color: "amber" },
          { label: "Pending", val: stats.pending || 0, color: "slate" },
          { label: "Completion", val: `${stats.completion_pct || 0}%`, color: "violet" },
        ].map(c => (
          <div key={c.label} className={cn("rounded-lg border p-2.5 text-center", t.card)}>
            <p className={cn("text-lg font-bold", dark ? `text-${c.color}-400` : `text-${c.color}-600`)}>{c.val}</p>
            <p className={cn("text-[9px] font-medium uppercase", t.textMuted)}>{c.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className={cn("flex items-center gap-2 flex-wrap mb-4 p-3 rounded-xl border", t.card)}>
        <div className="relative w-48"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><Input className={cn("h-8 pl-8 text-[12px]", t.inputBg)} placeholder="Search tasks..." value={gpSearch} onChange={e => setGpSearch(e.target.value)} data-testid="gp-search" /></div>
        {globalFilterOptions?.projects?.length > 0 && (
          <Select value={gpProject || "all"} onValueChange={v => setGpProject(v === "all" ? "" : v)}>
            <SelectTrigger className={cn("h-8 w-36 text-[12px]", t.inputBg)} data-testid="gp-filter-project"><SelectValue placeholder="All Projects" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Projects</SelectItem>{globalFilterOptions.projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {globalFilterOptions?.resources?.length > 0 && (
          <Select value={gpResource || "all"} onValueChange={v => setGpResource(v === "all" ? "" : v)}>
            <SelectTrigger className={cn("h-8 w-32 text-[12px]", t.inputBg)} data-testid="gp-filter-resource"><SelectValue placeholder="Resource" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Resources</SelectItem>{globalFilterOptions.resources.map(r => <SelectItem key={r.email} value={r.email}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {globalFilterOptions?.task_types?.length > 0 && (
          <Select value={gpType || "all"} onValueChange={v => setGpType(v === "all" ? "" : v)}>
            <SelectTrigger className={cn("h-8 w-28 text-[12px]", t.inputBg)} data-testid="gp-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{globalFilterOptions.task_types.map(tt => <SelectItem key={tt} value={tt}>{tt}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={gpStatus || "all"} onValueChange={v => setGpStatus(v === "all" ? "" : v)}>
          <SelectTrigger className={cn("h-8 w-28 text-[12px]", t.inputBg)} data-testid="gp-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="In Progress">In Progress</SelectItem><SelectItem value="Pending">Pending</SelectItem></SelectContent>
        </Select>
        <Select value={gpDueFilter || "all"} onValueChange={v => setGpDueFilter(v === "all" ? "" : v)}>
          <SelectTrigger className={cn("h-8 w-32 text-[12px]", t.inputBg)} data-testid="gp-filter-due"><SelectValue placeholder="Due Date" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Dates</SelectItem><SelectItem value="overdue">Overdue</SelectItem><SelectItem value="this_week">This Week</SelectItem><SelectItem value="next_week">Next Week</SelectItem><SelectItem value="no_date">No Due Date</SelectItem></SelectContent>
        </Select>
        {globalFilterOptions?.seasons?.length > 0 && (
          <Select value={gpSeason || "all"} onValueChange={v => setGpSeason(v === "all" ? "" : v)}>
            <SelectTrigger className={cn("h-8 w-28 text-[12px]", t.inputBg)} data-testid="gp-filter-season"><SelectValue placeholder="Season" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Seasons</SelectItem>{globalFilterOptions.seasons.map(s => <SelectItem key={s} value={s}>Season {s}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="relative ml-auto">
          <Button variant="ghost" size="sm" className={cn("h-8 text-[11px]", t.textSec)} onClick={() => setShowColToggle(!showColToggle)} data-testid="gp-col-toggle">
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />Columns
          </Button>
          {showColToggle && (
            <div className={cn("absolute right-0 top-9 z-50 w-44 rounded-lg border shadow-xl p-2 space-y-0.5", dark ? "bg-[#1a1d27] border-[#2a2d3a]" : "bg-white border-slate-200")} data-testid="gp-col-menu">
              {[["project","Project"],["season","Season"],["episode","Episode"],["task","Task"],["resource","Resource"],["language","Language"],["due_date","Due Date"],["duration","Duration"],["status","Status"],["po","PO"]].map(([key, label]) => (
                <label key={key} className={cn("flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-[11px] hover:bg-slate-100", dark && "hover:bg-[#2a2d3a]", t.text)}>
                  <input type="checkbox" checked={visibleCols[key]} onChange={() => toggleCol(key)} className="rounded border-slate-300 w-3 h-3" />
                  {label}
                </label>
              ))}
            </div>
          )}
        </div>
        {hasGpFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-[11px] text-red-500" onClick={() => { setGpSearch(""); setGpProject(""); setGpResource(""); setGpType(""); setGpStatus(""); setGpDueFilter(""); setGpSeason(""); }} data-testid="gp-clear-filters"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>
        )}
      </div>

      {/* Table */}
      <div className={cn("rounded-xl border overflow-hidden", t.card)}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={cn("border-b", t.border, t.surface)}>
              <tr>
                <th className={cn("px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-left", t.textMuted)}>#</th>
                {visibleCols.project && <SH label="Project" field="main_title" />}
                {visibleCols.season && <SH label="Season" field="season_number" />}
                {visibleCols.episode && <SH label="Episode" field="episode_title" />}
                {visibleCols.task && <SH label="Task" field="task_type" />}
                {visibleCols.resource && <SH label="Resource" field="resource_name" />}
                {visibleCols.language && <SH label="Language" field="language" />}
                {visibleCols.due_date && <SH label="Due Date" field="due_date" />}
                {visibleCols.duration && <SH label="Duration" field="duration" />}
                {visibleCols.status && <SH label="Status" field="status" />}
                {visibleCols.po && <SH label="PO" field="po_number" />}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={Object.values(visibleCols).filter(Boolean).length + 1} className="text-center py-10"><div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
              ) : tasks.length === 0 ? (
                <tr><td colSpan={Object.values(visibleCols).filter(Boolean).length + 1} className={cn("text-center py-10 text-sm", t.textSec)}>No tasks found</td></tr>
              ) : tasks.map((task, i) => (
                <tr key={task.id} className={cn("border-b transition-colors", t.border, t.hoverRow, task.is_overdue && (dark ? "bg-red-500/5" : "bg-red-50/50"))} data-testid={`gp-row-${task.id}`}>
                  <td className={cn("px-3 py-2 text-[11px]", t.textMuted)}>{(page - 1) * 50 + i + 1}</td>
                  {visibleCols.project && <td className="px-3 py-2"><p className={cn("text-[12px] font-medium truncate max-w-[160px]", t.text)}>{task.main_title}</p></td>}
                  {visibleCols.season && <td className={cn("px-3 py-2 text-[12px]", t.textSec)}>{task.season_number ? `S${task.season_number}` : "-"}</td>}
                  {visibleCols.episode && <td className={cn("px-3 py-2 text-[12px] max-w-[140px] truncate", t.textSec)}>{task.episode_title || "-"}</td>}
                  {visibleCols.task && <td className="px-3 py-2"><Badge variant="outline" className={cn("text-[10px]", dark && "border-[#2a2d3a] text-slate-400")}>{task.task_type || "-"}</Badge></td>}
                  {visibleCols.resource && <td className="px-3 py-2"><div className="flex items-center gap-1.5"><ResAvatar name={task.resource_name} email={task.resource} dark={dark} /><span className={cn("text-[12px] truncate max-w-[100px]", dark ? "text-slate-300" : "text-slate-700")}>{task.resource_name}</span></div></td>}
                  {visibleCols.language && <td className={cn("px-3 py-2 text-[12px]", t.textSec)}>{task.language || "-"}</td>}
                  {visibleCols.due_date && <td className={cn("px-3 py-2 text-[12px]", task.is_overdue ? "text-red-400 font-semibold" : t.textSec)}>{task.due_date || "-"}</td>}
                  {visibleCols.duration && <td className={cn("px-3 py-2 text-[12px]", t.textSec)}>{task.duration || "-"}</td>}
                  {visibleCols.status && <td className="px-3 py-2"><Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", statusBg(task.status, dark))}>{task.status}</Badge></td>}
                  {visibleCols.po && <td className={cn("px-3 py-2 text-[11px]", t.textMuted)}>{task.po_number || "-"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={cn("flex items-center justify-between px-4 py-3 border-t", t.border)}>
            <span className={cn("text-[11px]", t.textMuted)}>Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className={cn("h-7 text-[11px]", dark && "border-[#2a2d3a]")} disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="gp-prev">
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </Button>
              <span className={cn("text-[11px] px-2", t.textSec)}>Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" className={cn("h-7 text-[11px]", dark && "border-[#2a2d3a]")} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="gp-next">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


/* ═══════ RESOURCE CENTRE VIEW ═══════ */
const TrackerResourceCentre = ({ dark, headers }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRes, setSelectedRes] = useState(null);
  const [resDetail, setResDetail] = useState(null);
  const [resTasks, setResTasks] = useState([]);
  const [resLoading, setResLoading] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [emailType, setEmailType] = useState("assigned");
  const [emailDeadline, setEmailDeadline] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingWA, setSendingWA] = useState(false);
  const [editPersonalEmail, setEditPersonalEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [saving, setSaving] = useState(false);
  // Detail filters
  const [dtSearch, setDtSearch] = useState("");
  const [dtProject, setDtProject] = useState("");
  const [dtTaskType, setDtTaskType] = useState("");
  const [dtStatus, setDtStatus] = useState("");
  const [dtSort, setDtSort] = useState("due_date_desc");
  const t = T(dark);

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API}/tracker/resource-centre`, { headers })
      .then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const openResource = async (r) => {
    setSelectedRes(r);
    setSelectedTaskIds(new Set());
    setResLoading(true);
    try {
      const [tasksRes, detailRes] = await Promise.all([
        axios.get(`${API}/tracker/gp-projects?resource=${encodeURIComponent(r.email)}&page_size=500`, { headers }),
        axios.get(`${API}/tracker/resource-detail/${encodeURIComponent(r.email)}`, { headers }).catch(() => ({ data: null })),
      ]);
      setResTasks(tasksRes.data.tasks || []);
      setResDetail(detailRes.data);
      setEditPersonalEmail(detailRes.data?.personal_email || "");
      setEditPhone(detailRes.data?.phone_number || "");
    } catch { setResTasks([]); setResDetail(null); }
    finally { setResLoading(false); }
  };

  const toggleTask = (id) => setSelectedTaskIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAll = () => { if (selectedTaskIds.size === filteredResTasks.length) setSelectedTaskIds(new Set()); else setSelectedTaskIds(new Set(filteredResTasks.map(t => t.id))); };

  const handleSendEmail = async () => {
    if (selectedTaskIds.size === 0) return toast.error("Select tasks first");
    setSending(true);
    try {
      const res = await axios.post(`${API}/tracker/send-task-email`, {
        task_ids: Array.from(selectedTaskIds),
        email_type: emailType,
        deadline: emailDeadline || null,
      }, { headers });
      toast.success(`${res.data.sent_count} email(s) sent${res.data.failed_count > 0 ? `, ${res.data.failed_count} failed` : ""}`);
      if (res.data.failed?.length > 0) res.data.failed.forEach(f => toast.error(`${f.email}: ${f.reason}`));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  const handleSendWhatsApp = async () => {
    if (selectedTaskIds.size === 0) return toast.error("Select tasks first");
    setSendingWA(true);
    try {
      const res = await axios.post(`${API}/tracker/send-task-whatsapp`, { task_ids: Array.from(selectedTaskIds), message_type: emailType, deadline: emailDeadline || null }, { headers });
      toast.success(`${res.data.sent_count} WhatsApp message(s) sent`);
      if (res.data.failed?.length > 0) res.data.failed.forEach(f => toast.error(`${f.email}: ${f.reason}`));
    } catch (err) { toast.error(err.response?.data?.detail || "WhatsApp failed"); }
    finally { setSendingWA(false); }
  };

  const handleSaveInfo = async () => {
    if (!selectedRes) return;
    setSaving(true);
    try {
      const body = {};
      if (editPersonalEmail && !resDetail?.has_personal_email) body.personal_email = editPersonalEmail;
      if (editPhone && !resDetail?.has_phone) body.phone_number = editPhone;
      if (Object.keys(body).length === 0) { toast.info("Nothing to update"); setSaving(false); return; }
      const res = await axios.put(`${API}/tracker/resource-info/${encodeURIComponent(selectedRes.email)}`, body, { headers });
      toast.success(res.data.message);
      if (res.data.blocked?.length > 0) res.data.blocked.forEach(b => toast.warning(`Locked: ${b}`));
      const detailRes = await axios.get(`${API}/tracker/resource-detail/${encodeURIComponent(selectedRes.email)}`, { headers });
      setResDetail(detailRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const actLevel = (lu) => {
    if (!lu) return { label: "No Activity", color: "bg-slate-400" };
    const h = (Date.now() - new Date(lu).getTime()) / 36e5;
    if (h < 24) return { label: "Active", color: "bg-emerald-500" };
    if (h < 72) return { label: "Recent", color: "bg-amber-500" };
    return { label: "Idle", color: "bg-slate-400" };
  };

  // Detail task filter options
  const dtProjects = [...new Set(resTasks.map(t => t.main_title).filter(Boolean))].sort();
  const dtTaskTypes = [...new Set(resTasks.map(t => t.task_type).filter(Boolean))].sort();
  const parseDateForSort = (d) => {
    if (!d) return "0000-00-00";
    const s = String(d).trim();
    if (s.length >= 10 && s[4] === "-") return s.slice(0, 10);
    const parts = s.split(" ");
    if (parts.length >= 3) {
      const mo = {Jan:"01",Feb:"02",Mar:"03",Apr:"04",May:"05",Jun:"06",Jul:"07",Aug:"08",Sep:"09",Oct:"10",Nov:"11",Dec:"12"};
      const m = mo[parts[1]?.slice(0,3)];
      if (m) return `${parts[2]}-${m}-${parts[0].padStart(2,"0")}`;
    }
    return "0000-00-00";
  };
  const filteredResTasks = React.useMemo(() => {
    let list = resTasks;
    if (dtSearch) { const q = dtSearch.toLowerCase(); list = list.filter(t => (t.main_title||"").toLowerCase().includes(q) || (t.episode_title||"").toLowerCase().includes(q) || (t.po_number||"").toLowerCase().includes(q)); }
    if (dtProject) list = list.filter(t => t.main_title === dtProject);
    if (dtTaskType) list = list.filter(t => t.task_type === dtTaskType);
    if (dtStatus === "Completed") list = list.filter(t => t.status === "Completed");
    else if (dtStatus === "In Progress") list = list.filter(t => t.status !== "Completed");
    else if (dtStatus === "overdue") list = list.filter(t => t.is_overdue);
    const sorted = [...list];
    if (dtSort === "due_date_desc") sorted.sort((a, b) => parseDateForSort(b.due_date).localeCompare(parseDateForSort(a.due_date)));
    else if (dtSort === "due_date_asc") sorted.sort((a, b) => parseDateForSort(a.due_date).localeCompare(parseDateForSort(b.due_date)));
    else if (dtSort === "project") sorted.sort((a, b) => (a.main_title || "").localeCompare(b.main_title || ""));
    else if (dtSort === "status") sorted.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
    return sorted;
  }, [resTasks, dtSearch, dtProject, dtTaskType, dtStatus, dtSort]);

  const statusBgLocal = (s) => {
    if (s === "Completed") return dark ? "bg-emerald-500/10 text-emerald-400" : "bg-emerald-50 text-emerald-700";
    if (s === "In Progress") return dark ? "bg-blue-500/10 text-blue-400" : "bg-blue-50 text-blue-700";
    return dark ? "bg-slate-500/10 text-slate-400" : "bg-slate-50 text-slate-500";
  };

  const filtered = React.useMemo(() => {
    let list = data?.resources || [];
    if (search) { const q = search.toLowerCase(); list = list.filter(r => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)); }
    if (statusFilter === "active") list = list.filter(r => actLevel(r.last_translator_update).label === "Active");
    if (statusFilter === "issues") list = list.filter(r => r.issue_flagged > 0);
    if (statusFilter === "idle") list = list.filter(r => !r.last_translator_update);
    return list;
  }, [data, search, statusFilter]);

  if (loading) return <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin" /></div>;

  // ═══ Resource Detail + Email ═══
  if (selectedRes) {
    return (
      <div data-testid="tracker-rc-detail">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="outline" size="sm" className={cn("h-8 text-[11px]", dark && "border-[#2a2d3a] text-slate-300")} onClick={() => setSelectedRes(null)}>
            <ChevronLeft className="w-3.5 h-3.5 mr-1" />Back
          </Button>
          <div className="flex items-center gap-2 flex-1">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
              style={{ backgroundColor: `hsl(${[...(selectedRes.email||"")].reduce((a,c)=>a+c.charCodeAt(0),0)%360},55%,50%)` }}>
              {(selectedRes.name||"?")[0].toUpperCase()}
            </div>
            <div>
              <p className={cn("text-sm font-semibold", t.text)}>{selectedRes.name}</p>
              <p className={cn("text-[10px]", t.textMuted)}>{selectedRes.email} · {selectedRes.total_tasks} tasks · {selectedRes.completion_pct}% done</p>
            </div>
          </div>
        </div>

        {/* Resource Info (editable for missing fields) */}
        {resDetail && (
          <div className={cn("rounded-xl border p-4 mb-4", t.card)}>
            <p className={cn("text-[10px] font-semibold uppercase mb-3", t.textMuted)}>Resource Info</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={cn("text-[9px] font-medium uppercase mb-1 block", t.textMuted)}>Personal Email</label>
                {resDetail.has_personal_email ? (
                  <p className={cn("text-[12px]", t.text)}>{resDetail.personal_email} <CheckCircle2 className="w-3 h-3 inline text-emerald-500" /></p>
                ) : (
                  <input value={editPersonalEmail} onChange={e => setEditPersonalEmail(e.target.value)} placeholder="Enter personal email..."
                    className={cn("h-7 w-full px-2 rounded border text-[11px]", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300 placeholder:text-slate-600" : "bg-white border-slate-200")} />
                )}
              </div>
              <div>
                <label className={cn("text-[9px] font-medium uppercase mb-1 block", t.textMuted)}>Phone (WhatsApp)</label>
                {resDetail.has_phone ? (
                  <p className={cn("text-[12px]", t.text)}>{resDetail.phone_number} <CheckCircle2 className="w-3 h-3 inline text-emerald-500" /></p>
                ) : (
                  <input value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="+91..."
                    className={cn("h-7 w-full px-2 rounded border text-[11px]", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300 placeholder:text-slate-600" : "bg-white border-slate-200")} />
                )}
              </div>
            </div>
            {(!resDetail.has_personal_email || !resDetail.has_phone) && (
              <Button size="sm" className="mt-3 h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSaveInfo} disabled={saving}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                {saving ? "Saving..." : "Save Info"}
              </Button>
            )}
          </div>
        )}

        {/* Task Filters */}
        <div className={cn("rounded-xl border p-2.5 mb-3 flex items-center gap-2 flex-wrap", t.card)}>
          <div className="relative w-40"><Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" /><Input className={cn("h-7 pl-7 text-[11px]", t.inputBg)} placeholder="Search..." value={dtSearch} onChange={e => setDtSearch(e.target.value)} /></div>
          {dtProjects.length > 1 && (
            <select value={dtProject} onChange={e => setDtProject(e.target.value)} className={cn("h-7 px-1.5 rounded border text-[11px]", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300" : "bg-white border-slate-200")}>
              <option value="">All Projects</option>{dtProjects.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          {dtTaskTypes.length > 1 && (
            <select value={dtTaskType} onChange={e => setDtTaskType(e.target.value)} className={cn("h-7 px-1.5 rounded border text-[11px]", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300" : "bg-white border-slate-200")}>
              <option value="">All Types</option>{dtTaskTypes.map(tt => <option key={tt} value={tt}>{tt}</option>)}
            </select>
          )}
          <select value={dtStatus} onChange={e => setDtStatus(e.target.value)} className={cn("h-7 px-1.5 rounded border text-[11px]", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300" : "bg-white border-slate-200")}>
            <option value="">All Status</option><option value="Completed">Completed</option><option value="In Progress">In Progress</option><option value="overdue">Overdue</option>
          </select>
          <select value={dtSort} onChange={e => setDtSort(e.target.value)} className={cn("h-7 px-1.5 rounded border text-[11px]", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300" : "bg-white border-slate-200")}>
            <option value="due_date_desc">Due: Latest</option><option value="due_date_asc">Due: Earliest</option><option value="project">Project A-Z</option><option value="status">Status</option>
          </select>
          {(dtSearch || dtProject || dtTaskType || dtStatus) && (
            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-red-500" onClick={() => { setDtSearch(""); setDtProject(""); setDtTaskType(""); setDtStatus(""); }}><X className="w-3 h-3 mr-0.5" />Clear</Button>
          )}
          <span className={cn("text-[10px] ml-auto", t.textMuted)}>{filteredResTasks.length} tasks</span>
        </div>

        {/* Email/WhatsApp Actions */}
        <div className={cn("rounded-xl border p-2.5 mb-3 flex items-center gap-2 flex-wrap", t.card)}>
          <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={selectAll} data-testid="rc-select-all">
            {selectedTaskIds.size === filteredResTasks.length && filteredResTasks.length > 0 ? "Deselect All" : "Select All"}
          </Button>
          <span className={cn("text-[11px]", t.textMuted)}>{selectedTaskIds.size} selected</span>
          <div className="flex items-center gap-1.5 ml-auto">
            <select value={emailType} onChange={e => setEmailType(e.target.value)}
              className={cn("h-7 px-2 rounded border text-[11px]", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300" : "bg-white border-slate-200")}>
              <option value="assigned">Assigned</option>
              <option value="reminder">Reminder</option>
              <option value="overdue">Overdue</option>
            </select>
            <input type="date" value={emailDeadline} onChange={e => setEmailDeadline(e.target.value)} placeholder="Deadline"
              className={cn("h-7 px-2 rounded border text-[11px] w-32", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300" : "bg-white border-slate-200")} />
            <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700 text-white gap-1" onClick={handleSendEmail} disabled={sending || selectedTaskIds.size === 0} data-testid="rc-send-email">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              {sending ? "Sending..." : "Email"}
            </Button>
            <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={handleSendWhatsApp} disabled={sendingWA || selectedTaskIds.size === 0} data-testid="rc-send-whatsapp">
              {sendingWA ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
              {sendingWA ? "Sending..." : "WhatsApp"}
            </Button>
          </div>
        </div>

        {/* Tasks Table */}
        <div className={cn("rounded-xl border overflow-hidden", t.card)}>
          {resLoading ? (
            <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-3 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={cn("border-b", t.border, t.surface)}>
                  <tr>
                    <th className={cn("px-2 py-2 w-8", t.textMuted)}></th>
                    <th className={cn("px-3 py-2 text-left text-[10px] font-semibold uppercase", t.textMuted)}>Project</th>
                    <th className={cn("px-3 py-2 text-left text-[10px] font-semibold uppercase", t.textMuted)}>Episode</th>
                    <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Task</th>
                    <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Language</th>
                    <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Due Date</th>
                    <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Status</th>
                    <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>PO</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResTasks.map((task, i) => (
                    <tr key={task.id} className={cn("border-b transition-colors", t.border, selectedTaskIds.has(task.id) ? (dark ? "bg-blue-500/10" : "bg-blue-50") : t.hoverRow)}
                      onClick={() => toggleTask(task.id)} style={{cursor:"pointer"}}>
                      <td className="px-2 py-2 text-center"><input type="checkbox" checked={selectedTaskIds.has(task.id)} onChange={() => {}} className="rounded w-3 h-3" /></td>
                      <td className="px-3 py-2">
                        <p className={cn("text-[12px] font-medium", t.text)}>{task.main_title}</p>
                        {task.season_number && <span className={cn("text-[9px]", t.textMuted)}>S{task.season_number}</span>}
                      </td>
                      <td className={cn("px-3 py-2 text-[12px]", t.textSec)}>{task.episode_title || "-"}</td>
                      <td className="px-3 py-2 text-center"><Badge variant="outline" className={cn("text-[10px]", dark && "border-[#2a2d3a] text-slate-400")}>{task.task_type || "-"}</Badge></td>
                      <td className={cn("px-3 py-2 text-center text-[12px]", t.textSec)}>{task.language || "-"}</td>
                      <td className={cn("px-3 py-2 text-center text-[11px]", task.is_overdue ? "text-red-400 font-semibold" : t.textSec)}>{task.due_date || "-"}</td>
                      <td className="px-3 py-2 text-center"><Badge className={cn("text-[10px] border-0", statusBgLocal(task.status))}>{task.status}</Badge></td>
                      <td className={cn("px-3 py-2 text-center text-[11px]", t.textMuted)}>{task.po_number || "-"}</td>
                    </tr>
                  ))}
                  {filteredResTasks.length === 0 && !resLoading && (
                    <tr><td colSpan={8} className={cn("text-center py-10 text-sm", t.textSec)}>No tasks found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══ Resource List ═══
  return (
    <div data-testid="tracker-resource-centre">
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { label: "Resources", val: data?.total_resources || 0, color: "blue" },
          { label: "Active (24h)", val: (data?.resources || []).filter(r => actLevel(r.last_translator_update).label === "Active").length, color: "emerald" },
          { label: "Issues", val: data?.total_issues || 0, color: "red" },
          { label: "No Activity", val: (data?.resources || []).filter(r => !r.last_translator_update).length, color: "slate" },
        ].map(c => (
          <div key={c.label} className={cn("rounded-lg border p-3 text-center", t.card)}>
            <p className={cn("text-lg font-bold", `text-${c.color}-500`)}>{c.val}</p>
            <p className={cn("text-[9px] uppercase", t.textMuted)}>{c.label}</p>
          </div>
        ))}
      </div>
      <div className={cn("rounded-xl border p-2.5 mb-3 flex items-center gap-2", t.card)}>
        <div className="relative flex-1 max-w-xs"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><Input className={cn("h-8 pl-8 text-[12px]", t.inputBg)} placeholder="Search resource..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={cn("h-8 px-2 rounded border text-[11px]", dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-300" : "bg-white border-slate-200")}>
          <option value="all">All Resources</option><option value="active">Active (24h)</option><option value="issues">Has Issues</option><option value="idle">No Activity</option>
        </select>
        <Button variant="ghost" size="sm" className={cn("h-8 text-[11px] ml-auto", t.textSec)} onClick={fetchData}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
      </div>
      <div className={cn("rounded-lg border overflow-hidden", t.card)}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={cn("border-b", t.border, t.surface)}>
              <tr>
                <th className={cn("px-3 py-2 text-left text-[10px] font-semibold uppercase", t.textMuted)}>#</th>
                <th className={cn("px-3 py-2 text-left text-[10px] font-semibold uppercase", t.textMuted)}>Resource</th>
                <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Tasks</th>
                <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Projects</th>
                <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Status</th>
                <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Issues</th>
                <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Progress</th>
                <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Activity</th>
                <th className={cn("px-3 py-2 text-center text-[10px] font-semibold uppercase", t.textMuted)}>Avail</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const act = actLevel(r.last_translator_update);
                return (
                  <tr key={r.email} className={cn("border-b transition-colors cursor-pointer", t.border, t.hoverRow)}
                    onClick={() => openResource(r)}>
                    <td className={cn("px-3 py-2 text-[11px]", t.textMuted)}>{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                            style={{ backgroundColor: `hsl(${[...(r.email||"")].reduce((a,c)=>a+c.charCodeAt(0),0)%360},55%,50%)` }}>
                            {(r.name||"?")[0].toUpperCase()}
                          </div>
                          <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${dark?"border-[#12151e]":"border-white"} ${act.color}`} />
                        </div>
                        <div>
                          <p className={cn("text-[12px] font-medium", t.text)}>{r.name}</p>
                          <p className={cn("text-[10px]", t.textMuted)}>{r.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className={cn("px-3 py-2 text-center text-[12px] font-semibold", t.text)}>{r.total_tasks}</td>
                    <td className={cn("px-3 py-2 text-center text-[11px]", t.textSec)}>{r.project_count}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="text-[10px] text-emerald-500 font-medium">{r.completed}</span>
                      <span className={cn("text-[10px] mx-0.5", t.textMuted)}>/</span>
                      <span className="text-[10px] text-amber-500">{r.in_progress}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.issue_flagged > 0 ? <Badge className="bg-red-500/10 text-red-400 border-0 text-[9px]">{r.issue_flagged}</Badge> : <span className={cn("text-[10px]", t.textMuted)}>-</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <div className={cn("w-12 h-1.5 rounded-full overflow-hidden", dark?"bg-slate-700":"bg-slate-100")}>
                          <div className="h-full bg-emerald-500 rounded-full" style={{width:`${r.completion_pct}%`}} />
                        </div>
                        <span className={cn("text-[10px] w-8", t.textMuted)}>{r.completion_pct}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <div className={`w-2 h-2 rounded-full ${act.color}`} />
                        <span className={cn("text-[10px]", t.textMuted)}>{act.label}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.available === false ? (
                        <div title={r.unavailable_reason || "Unavailable"}>
                          <div className="w-2 h-2 rounded-full bg-red-400 mx-auto" />
                          <span className={cn("text-[9px] text-red-400")}>{r.unavailable_until ? `Till ${r.unavailable_until}` : "Off"}</span>
                        </div>
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-emerald-400 mx-auto" title={r.schedule || "Available"} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


/* ═══════ PROJECT DETAIL PANEL ═══════ */
const ProjectDetailPanel = ({ project, onClose, dark }) => {
  const { getAuthHeaders } = useTrackerAuth();
  const [detail, setDetail] = useState(null); const [loading, setLoading] = useState(true); const [expandedEps, setExpandedEps] = useState({});
  const t = T(dark);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/tracker/projects/${encodeURIComponent(project.title)}/details`, { headers: getAuthHeaders() })
      .then(r => setDetail(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [project.title]);
  if (loading) return <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" onClick={onClose}><div className={cn("rounded-2xl p-8",t.card,t.text)}>Loading...</div></div>;
  if (!detail) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose} data-testid="project-detail-panel">
      <div className={cn("w-full max-w-3xl h-full overflow-y-auto shadow-2xl", dark?"bg-[#0f1117]":"bg-white")} onClick={e=>e.stopPropagation()}>
        <div className={cn("sticky top-0 border-b px-6 py-4 flex items-center justify-between z-10", dark?"bg-[#0f1117] border-[#2a2d3a]":"bg-white border-slate-200")}>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-detail-btn"><ChevronLeft className="w-4 h-4"/></Button>
            <div><h2 className={cn("text-lg font-bold",t.text)}>{detail.title}</h2><p className={cn("text-xs",t.textSec)}>{detail.total_episodes} episodes · {detail.total_tasks} tasks</p></div>
          </div>
          <CircularProgress pct={detail.completion_pct} size={48} dark={dark} />
        </div>
        <div className="grid grid-cols-4 gap-3 px-6 py-4">
          {[{v:detail.episodes?.reduce((s,e)=>s+e.completed,0),l:"Completed",c:"emerald"},{v:detail.episodes?.reduce((s,e)=>s+e.in_progress,0),l:"In Progress",c:"blue"},{v:detail.episodes?.reduce((s,e)=>s+e.pending,0),l:"Pending",c:"slate"},{v:detail.episodes?.reduce((s,e)=>s+e.overdue_count,0),l:"Overdue",c:"amber"}].map(s=>(
            <div key={s.l} className={cn("text-center p-3 rounded-xl",dark?`bg-${s.c}-500/10`:`bg-${s.c}-50`)}>
              <p className={cn("text-xl font-bold",dark?`text-${s.c}-400`:`text-${s.c}-700`)}>{s.v}</p>
              <p className={cn("text-[10px] uppercase tracking-wide mt-0.5",dark?`text-${s.c}-400/70`:`text-${s.c}-600`)}>{s.l}</p>
            </div>
          ))}
        </div>
        <div className="px-6 pb-6 space-y-2">
          <h3 className={cn("text-sm font-semibold mb-2",t.text)}>Episodes</h3>
          {detail.episodes?.map(ep => (
            <div key={ep.episode} className={cn("border rounded-lg overflow-hidden",t.border)}>
              <div className={cn("flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",t.surface,t.hoverRow)}
                onClick={()=>setExpandedEps(prev=>({...prev,[ep.episode]:!prev[ep.episode]}))}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {expandedEps[ep.episode] ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0"/> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0"/>}
                  <p className={cn("text-[13px] font-medium truncate",dark?"text-slate-300":"text-slate-700")}>{ep.episode}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex -space-x-1">{ep.resources?.slice(0,3).map(r=><ResAvatar key={r.email} name={r.name} email={r.email} dark={dark}/>)}</div>
                  <span className={cn("text-[11px]",dark?"text-emerald-400":"text-emerald-600")}>{ep.completed}</span><span className="text-[11px] text-slate-400">/</span><span className={cn("text-[11px]",t.textSec)}>{ep.total_tasks}</span>
                  <div className="w-16"><Progress value={ep.completion_pct} className="h-1.5"/></div>
                </div>
              </div>
              {expandedEps[ep.episode] && (
                <div className={cn("border-t",t.border)}>
                  <table className="w-full">
                    <thead className={t.surface}><tr>{["Type","Resource","Language","Due","Status"].map(h=><th key={h} className={cn("px-3 py-2 text-[10px] font-semibold uppercase text-left",t.textMuted)}>{h}</th>)}</tr></thead>
                    <tbody>{ep.tasks?.map(task=>(
                      <tr key={task.id} className={cn("border-t",t.border,t.hoverRow)}>
                        <td className={cn("px-3 py-2 text-[12px]",dark?"text-slate-300":"text-slate-700")}>{task.task_type}</td>
                        <td className="px-3 py-2"><div className="flex items-center gap-1.5"><ResAvatar name={task.resource_name} email={task.resource} dark={dark}/><span className={cn("text-[12px]",t.textSec)}>{task.resource_name}</span></div></td>
                        <td className={cn("px-3 py-2 text-[12px]",t.textSec)}>{task.language}</td>
                        <td className={cn("px-3 py-2 text-[12px]",task.due_date && task.due_date < new Date().toISOString().slice(0,10) && task.status !== "Completed" ? "text-red-400 font-semibold" : t.textSec)}>{task.due_date||"-"}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className={cn("text-[10px] px-1.5 py-0",statusBg(task.status,dark))}>{task.status}</Badge></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════ */
export default function TrackerDashboard() {
  const { user, logout, getAuthHeaders } = useTrackerAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("tracker_theme") === "dark");
  const [view, setView] = useState("overview");
  const [projects, setProjects] = useState([]);
  const [kanbanData, setKanbanData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [workload, setWorkload] = useState(null);
  const [filters, setFilters] = useState(null);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeSnap, setActiveSnap] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filters
  const [search, setSearch] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [filterResource, setFilterResource] = useState("");
  const [filterTaskType, setFilterTaskType] = useState("");
  const [filterLanguage, setFilterLanguage] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Upload
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [replaceMode, setReplaceMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  // Snapshots
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [snapshots, setSnapshots] = useState([]);

  const t = T(dark);
  const headers = getAuthHeaders();

  const toggleTheme = () => { setDark(d => { const n = !d; localStorage.setItem("tracker_theme", n?"dark":"light"); return n; }); };

  useEffect(() => { if (!user) return; axios.get(`${API}/tracker/filters`, { headers }).then(r => setFilters(r.data)).catch(()=>{}); }, [user, refreshKey]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (filterResource) params.resource = filterResource;
    if (filterTaskType) params.task_type = filterTaskType;
    if (filterLanguage) params.language = filterLanguage;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    try {
      const projRes = await axios.get(`${API}/tracker/projects`, { params, headers });
      setProjects(projRes.data.projects || []);
      setKpis(projRes.data.kpis || {});
      setActiveSnap(projRes.data.active_snapshot || null);

      if (view === "overview") {
        const wRes = await axios.get(`${API}/tracker/workload`, { params, headers });
        setWorkload(wRes.data);
      } else if (view === "kanban") {
        const kp = {...params}; if (filterProject) kp.project = filterProject;
        const res = await axios.get(`${API}/tracker/kanban`, { params: kp, headers });
        setKanbanData(res.data);
      } else if (view === "timeline") {
        const res = await axios.get(`${API}/tracker/timeline`, { params, headers });
        setTimeline(res.data.timeline || []);
      }
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  }, [view, search, filterProject, filterResource, filterTaskType, filterLanguage, fromDate, toDate, user, refreshKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (taskId, newStatus) => {
    try { await axios.put(`${API}/tracker/tasks/${taskId}/status?status=${encodeURIComponent(newStatus)}`, null, { headers }); toast.success(`Moved to ${newStatus}`); fetchData(); } catch { toast.error("Failed"); }
  };

  const handleUpload = async () => {
    if (!uploadFile) return; setUploading(true); setUploadProgress("");
    try {
      const CHUNK_SIZE = 512 * 1024; // 512KB chunks
      const totalChunks = Math.ceil(uploadFile.size / CHUNK_SIZE);

      if (totalChunks <= 1) {
        // Small file — direct upload
        setUploadProgress("Uploading...");
        const fd = new FormData(); fd.append("file", uploadFile);
        const res = await axios.post(`${API}/tracker/upload?replace=${replaceMode}&name=${encodeURIComponent(uploadName || uploadFile.name)}`, fd, { headers, timeout: 120000 });
        toast.success(`Imported ${res.data.tasks_imported} tasks`);
      } else {
        // Large file — chunked upload
        const uploadId = `upload_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
        for (let i = 0; i < totalChunks; i++) {
          setUploadProgress(`Uploading chunk ${i + 1}/${totalChunks}...`);
          const start = i * CHUNK_SIZE;
          const chunk = uploadFile.slice(start, start + CHUNK_SIZE);
          const fd = new FormData();
          fd.append("chunk", chunk);
          fd.append("upload_id", uploadId);
          fd.append("chunk_index", String(i));
          fd.append("total_chunks", String(totalChunks));
          fd.append("filename", uploadFile.name);
          await axios.post(`${API}/tracker/upload-chunk`, fd, { headers, timeout: 60000 });
        }
        // Finalize
        setUploadProgress("Processing file...");
        const res = await axios.post(`${API}/tracker/upload-finalize`, {
          upload_id: uploadId, replace: replaceMode, name: uploadName || uploadFile.name,
        }, { headers, timeout: 120000 });
        toast.success(`Imported ${res.data.tasks_imported} tasks`);
      }
      setShowUpload(false); setUploadFile(null); setUploadName(""); setRefreshKey(k=>k+1);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || (err.code === "ECONNABORTED" ? "Upload timed out — try a smaller file" : "Upload failed"));
    }
    setUploading(false); setUploadProgress("");
  };

  const loadSnapshots = async () => { try { const res = await axios.get(`${API}/tracker/snapshots`, { headers }); setSnapshots(res.data.snapshots || []); } catch {} setShowSnapshots(true); };
  const activateSnapshot = async (id) => { try { await axios.put(`${API}/tracker/snapshots/${id}/activate`, null, { headers }); toast.success("Activated"); setRefreshKey(k=>k+1); setShowSnapshots(false); } catch { toast.error("Failed"); } };
  const deleteSnapshot = async (id) => { try { await axios.delete(`${API}/tracker/snapshots/${id}`, { headers }); toast.success("Deleted"); loadSnapshots(); setRefreshKey(k=>k+1); } catch { toast.error("Failed"); } };
  const handleLogout = () => { logout(); navigate("/tracker/login"); };

  const viewModes = [
    { id: "overview", label: "In Progress", icon: Users },
    { id: "kanban", label: "Kanban", icon: Columns3 },
    { id: "timeline", label: "Timeline", icon: GanttChart },
    { id: "table", label: "Table", icon: Table2 },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "gp-projects", label: "GP Projects", icon: FolderKanban },
    { id: "resource-centre", label: "Resources", icon: Users },
  ];

  const hasFilters = search || filterResource || filterTaskType || filterLanguage || filterProject || fromDate || toDate;

  if (!user) { navigate("/tracker/login"); return null; }

  return (
    <div className={cn("min-h-screen transition-colors duration-300", t.bg)} data-testid="tracker-dashboard">
      {/* Top Bar */}
      <header className={cn("sticky top-0 z-40 border-b backdrop-blur-xl", dark?"bg-[#0f1117]/90 border-[#2a2d3a]":"bg-white/90 border-slate-200")}>
        <div className="max-w-[1600px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20"><Crosshair className="w-5 h-5 text-white"/></div>
            <div><h1 className={cn("text-sm font-bold tracking-tight",t.text)} style={{fontFamily:"Manrope, sans-serif"}}>Project Tracker</h1><p className={cn("text-[10px]",t.textMuted)}>Graphikpro</p></div>
          </div>
          <div className="flex items-center gap-2">
            {activeSnap && <Badge variant="outline" className={cn("text-[10px]",dark?"border-[#2a2d3a] text-slate-400":"")}>{activeSnap.name||"Active"}</Badge>}
            <Button variant="outline" size="sm" className={cn("h-8 text-[11px] gap-1.5",dark&&"border-[#2a2d3a] text-slate-300 hover:bg-[#1a1d27]")} onClick={()=>setShowUpload(true)} data-testid="upload-btn"><Upload className="w-3.5 h-3.5"/>Upload Excel</Button>
            <Button variant="ghost" size="sm" className={cn("h-8 text-[11px]",dark&&"text-slate-400 hover:text-white")} onClick={loadSnapshots} data-testid="snapshots-btn"><Layers className="w-3.5 h-3.5"/></Button>
            <button onClick={toggleTheme} className={cn("w-8 h-8 rounded-lg flex items-center justify-center transition-colors",dark?"bg-[#1a1d27] text-amber-400 hover:bg-[#2a2d3a]":"bg-slate-100 text-slate-600 hover:bg-slate-200")} data-testid="theme-toggle">{dark?<Sun className="w-4 h-4"/>:<Moon className="w-4 h-4"/>}</button>
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-slate-200/20">
              <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold uppercase",dark?"bg-blue-500/20 text-blue-400":"bg-blue-100 text-blue-600")}>{user.display_name?.charAt(0)||user.email?.charAt(0)}</div>
              <span className={cn("text-[12px] font-medium hidden sm:inline",t.text)}>{user.display_name||user.email}</span>
              <button onClick={handleLogout} className={cn("p-1.5 rounded-lg transition-colors",dark?"text-slate-500 hover:text-red-400":"text-slate-400 hover:text-red-500")} data-testid="tracker-logout"><LogOut className="w-4 h-4"/></button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-5" data-testid="kpi-strip">
          <KpiCard label="Projects" value={projects.length||"0"} icon={FolderOpen} color="violet" dark={dark}/>
          <KpiCard label="Total Tasks" value={kpis.total_tasks?.toLocaleString()||"0"} icon={Table2} color="blue" dark={dark}/>
          <KpiCard label="Completed" value={kpis.completed_tasks?.toLocaleString()||"0"} sub={`${kpis.completion_pct||0}%`} icon={CheckCircle2} color="emerald" dark={dark}/>
          <KpiCard label="Overdue" value={kpis.overdue_tasks||"0"} icon={AlertTriangle} color="red" dark={dark}/>
          <KpiCard label="Resources" value={kpis.active_resources||"0"} icon={Users} color="amber" dark={dark}/>
        </div>

        {/* Toolbar */}
        <div className={cn("mb-5 rounded-xl border overflow-hidden", t.card)} data-testid="toolbar">
          {/* Row 1: View Tabs */}
          <div className={cn("flex items-center gap-2 px-3 py-2.5 border-b", t.border)}>
            <div className={cn("flex items-center gap-1 rounded-lg p-1", dark ? "bg-[#13151d]" : "bg-slate-100")}>
              {viewModes.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setView(id)}
                  className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all",
                    view === id ? (dark ? "bg-[#1a1d27] text-white shadow-sm" : "bg-white text-slate-900 shadow-sm") : (dark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"))}
                  data-testid={`view-${id}`}><Icon className="w-3.5 h-3.5" />{label}</button>
              ))}
            </div>
          </div>
          {/* Row 2: Filters - hidden on Resources tab */}
          {view !== "resource-centre" && (
          <div className="flex items-center gap-2 px-3 py-2.5 overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
            <div className="relative w-44 shrink-0"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><Input className={cn("h-8 pl-8 text-[12px]", t.inputBg)} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} data-testid="filter-search" /></div>
            <div className={cn("w-px h-6 shrink-0", dark ? "bg-[#2a2d3a]" : "bg-slate-200")} />
            <DatePickerButton value={fromDate} onChange={setFromDate} placeholder="From date" dark={dark} />
            <span className={cn("text-[10px] shrink-0", t.textMuted)}>to</span>
            <DatePickerButton value={toDate} onChange={setToDate} placeholder="To date" dark={dark} />
            <div className={cn("w-px h-6 shrink-0", dark ? "bg-[#2a2d3a]" : "bg-slate-200")} />
            {view === "kanban" && filters?.projects?.length > 0 && (
              <Select value={filterProject || "all_projects"} onValueChange={v => setFilterProject(v === "all_projects" ? "" : v)}>
                <SelectTrigger className={cn("h-8 w-36 text-[12px] shrink-0", t.inputBg)} data-testid="filter-project"><SelectValue placeholder="All Projects" /></SelectTrigger>
                <SelectContent><SelectItem value="all_projects">All Projects</SelectItem>{filters.projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {filters?.resources?.length > 0 && (
              <Select value={filterResource || "all_resources"} onValueChange={v => setFilterResource(v === "all_resources" ? "" : v)}>
                <SelectTrigger className={cn("h-8 w-36 text-[12px] shrink-0", t.inputBg)} data-testid="filter-resource"><SelectValue placeholder="All Resources" /></SelectTrigger>
                <SelectContent><SelectItem value="all_resources">All Resources</SelectItem>{filters.resources.map(r => <SelectItem key={r.email} value={r.email}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {filters?.task_types?.length > 0 && (
              <Select value={filterTaskType || "all_types"} onValueChange={v => setFilterTaskType(v === "all_types" ? "" : v)}>
                <SelectTrigger className={cn("h-8 w-32 text-[12px] shrink-0", t.inputBg)} data-testid="filter-task-type"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent><SelectItem value="all_types">All Types</SelectItem>{filters.task_types.map(tt => <SelectItem key={tt} value={tt}>{tt}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-8 text-[11px] text-red-500 shrink-0" onClick={() => { setSearch(""); setFilterResource(""); setFilterTaskType(""); setFilterLanguage(""); setFilterProject(""); setFromDate(""); setToDate(""); }} data-testid="clear-filters"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>
            )}
          </div>
          )}
        </div>

        {/* Views */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"/></div>
        ) : (
          <>
            {view==="overview" && <OverviewView workload={workload} projects={projects} kpis={kpis} dark={dark} onSelectProject={setSelectedProject}/>}
            {view==="kanban" && <KanbanView kanbanData={kanbanData} onStatusChange={handleStatusChange} dark={dark}/>}
            {view==="timeline" && <TimelineView timeline={timeline} dark={dark}/>}
            {view==="table" && <TableView projects={projects} onSelect={setSelectedProject} dark={dark}/>}
            {view==="analytics" && <AnalyticsView dark={dark} headers={headers} globalFilters={{ search, resource: filterResource, task_type: filterTaskType, language: filterLanguage, from_date: fromDate, to_date: toDate }}/>}
            {view==="gp-projects" && <GPProjectsView dark={dark} headers={headers} filters={filters} globalFilters={{ search, resource: filterResource, task_type: filterTaskType, language: filterLanguage, from_date: fromDate, to_date: toDate }}/>}
            {view==="resource-centre" && <TrackerResourceCentre dark={dark} headers={headers} />}
          </>
        )}
      </main>

      {selectedProject && <ProjectDetailPanel project={selectedProject} onClose={()=>setSelectedProject(null)} dark={dark}/>}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className={cn("max-w-md",dark&&"bg-[#1a1d27] border-[#2a2d3a] text-white")}>
          <DialogHeader><DialogTitle className={t.text}>Upload Task Sheet</DialogTitle><DialogDescription className={t.textSec}>Import an Excel file to populate the tracker</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><label className={cn("text-xs font-medium mb-1.5 block",t.textSec)}>Snapshot Name</label><Input className={cn("h-9",t.inputBg)} value={uploadName} onChange={e=>setUploadName(e.target.value)} placeholder="e.g. March 2026" data-testid="upload-name"/></div>
            <div>
              <label className={cn("text-xs font-medium mb-1.5 block",t.textSec)}>Excel File</label>
              <div className={cn("border-2 border-dashed rounded-xl p-6 text-center transition-colors",dark?"border-[#2a2d3a] hover:border-blue-500/40":"border-slate-200 hover:border-blue-300",uploadFile&&(dark?"border-blue-500/40 bg-blue-500/5":"border-blue-300 bg-blue-50"))}>
                <input type="file" accept=".xlsx,.xls,.csv" onChange={e=>setUploadFile(e.target.files?.[0]||null)} className="hidden" id="tracker-file-input" data-testid="upload-file-input"/>
                <label htmlFor="tracker-file-input" className="cursor-pointer">
                  {uploadFile ? <div className="flex items-center justify-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-500"/><span className={cn("text-sm font-medium",t.text)}>{uploadFile.name}</span></div>
                  : <><Upload className={cn("w-8 h-8 mx-auto mb-2",t.textMuted)}/><p className={cn("text-sm",t.textSec)}>Click to select</p><p className={cn("text-[10px] mt-1",t.textMuted)}>.xlsx, .xls, .csv</p></>}
                </label>
              </div>
            </div>
            <div className="flex items-center gap-2"><input type="checkbox" id="replace-mode" checked={replaceMode} onChange={e=>setReplaceMode(e.target.checked)} className="rounded" data-testid="upload-replace"/><label htmlFor="replace-mode" className={cn("text-xs",t.textSec)}>Replace all existing data</label></div>
            <Button onClick={handleUpload} disabled={!uploadFile||uploading} className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white" data-testid="upload-submit">
              {uploading?<><RefreshCw className="w-4 h-4 mr-2 animate-spin"/>{uploadProgress || "Uploading..."}</>:<><Upload className="w-4 h-4 mr-2"/>Upload & Import</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Snapshots Dialog */}
      <Dialog open={showSnapshots} onOpenChange={setShowSnapshots}>
        <DialogContent className={cn("max-w-lg",dark&&"bg-[#1a1d27] border-[#2a2d3a] text-white")}>
          <DialogHeader><DialogTitle className={t.text}>Data Snapshots</DialogTitle><DialogDescription className={t.textSec}>Manage uploaded datasets</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {snapshots.length===0 && <p className={cn("text-sm text-center py-8",t.textSec)}>No snapshots yet</p>}
            {snapshots.map(s=>(
              <div key={s.id} className={cn("flex items-center justify-between p-3 rounded-lg border",t.border,s.is_active&&(dark?"border-blue-500/40 bg-blue-500/5":"border-blue-300 bg-blue-50"))}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><p className={cn("text-sm font-medium truncate",t.text)}>{s.name}</p>{s.is_active&&<Badge className="text-[9px] bg-blue-500 text-white">Active</Badge>}</div>
                  <p className={cn("text-[11px] mt-0.5",t.textMuted)}>{s.task_count} tasks · {s.uploaded_by} · {new Date(s.uploaded_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!s.is_active&&<Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={()=>activateSnapshot(s.id)}><Check className="w-3.5 h-3.5 mr-1"/>Activate</Button>}
                  <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:text-red-600" onClick={()=>deleteSnapshot(s.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
